<script setup>
import { getInboxLabel } from 'dashboard/helper/inboxLabel';
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import AgentsAPI from 'dashboard/api/agents';
import ContactsAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import EventDateTimeFields from './EventDateTimeFields.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import MessageAPI from 'dashboard/api/inbox/message';
import { appointmentMessage } from './appointmentMessage';
import {
  CALENDAR_TIME_ZONE_LABEL,
  calendarInstant,
  calendarISO,
  calendarFormat,
  calendarDateKey,
  calendarParts,
  calendarDate,
  calendarAt,
  calendarDayStart,
  calendarPickerDate,
  calendarDayEnd,
  shiftCalendarDays,
  shiftCalendarMonths,
  moveCalendarPeriod,
} from './calendarTime';
import TalkviaCalendarEventsAPI from 'dashboard/api/talkviaCalendarEvents';
import TalkviaCalendarIntegrationAPI from 'dashboard/api/talkviaCalendarIntegration';
import TalkviaInventoryProductsAPI from 'dashboard/api/talkviaInventoryProducts';
import { ContractsAPI } from 'dashboard/api/talkviaContracts';
import { buildContractCalendarEvents } from './contractCalendarEvents';
import { formatContactPhone } from 'dashboard/components-next/Contacts/ContactsCard/contactListPresentation';
import { dayQuantityTotals, formatDayTotals, isDayNote } from './dayQuantities';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { isAdmin } = useAdmin();
const inboxes = useMapGetter('inboxes/getInboxes');
const AUTO_REFRESH_INTERVAL_MS = 20000;

const copy = {
  title: 'Agenda',
  subtitle:
    'Organize retornos, reuniões e compromissos vindos das conversas, propostas e integrações externas.',
  actions: {
    newEvent: 'Novo evento',
    create: 'Criar',
    saveEvent: 'Salvar evento',
    sendConversation: 'Conversa para enviar o agendamento',
    loadingConversations: 'Carregando conversas…',
    loadingEvents: 'Carregando eventos…',
    deleteEvent: 'Excluir evento',
    cancel: 'Cancelar',
    edit: 'Editar',
    sync: 'Sincronizar',
    menu: 'Menu',
    search: 'Pesquisar',
    clearSearch: 'Limpar busca',
    clearFilters: 'Limpar',
    day: 'Dia',
    week: 'Semana',
    month: 'Mês',
    year: 'Ano',
    schedule: 'Programação',
    fourDays: '4 dias',
    today: 'Hoje',
    previous: 'Anterior',
    next: 'Próximo',
    filter: 'Filtrar',
    filters: 'Filtros',
    applyFilters: 'Aplicar filtros',
    apply: 'Aplicar',
    configureGoogle: 'Configurar Google Agenda',
    changeContact: 'Trocar',
    removeContact: 'Remover contato',
    openConversation: 'Abrir conversa',
    close: 'Fechar',
  },
  fields: {
    title: 'Título',
    description: 'Descrição',
    location: 'Local',
    start: 'Início',
    end: 'Fim',
    period: 'Período do evento',
    status: 'Status',
    source: 'Origem',
    assignee: 'Responsável',
    inbox: 'Caixa de entrada',
    contact: 'Contato',
    contactSearch: 'Buscar contato',
    inventoryProduct: 'Produto do serviço',
    inventoryQuantity: 'Quantidade para baixa',
    createGoogleMeet: 'Criar link Google Meet',
    attendeeEmails: 'Convidados por e-mail',
    googleMeetLink: 'Link Google Meet',
    allDay: 'Dia inteiro',
  },
  sections: {
    filters: 'Filtros',
    upcoming: 'Próximos eventos',
    eventDetails: 'Detalhes do evento',
    eventDetailsDescription:
      'Informe o assunto e quando o compromisso acontece.',
    organization: 'Organização',
    organizationDescription:
      'Vincule o evento ao atendimento e ao local correto.',
    service: 'Serviço',
    serviceDescription:
      'Vincule um item do catálogo e informe a quantidade que será baixada.',
    meeting: 'Videoconferência',
    googleMeet: 'Google Meet',
    meetingDescription:
      'Gere um link do Google Meet e envie o convite aos participantes.',
    notes: 'Observações',
    googleIntegration: 'Integração do Google Agenda',
  },
  labels: {
    dayTotal: 'Total do dia',
    dayNote: 'Anotação do dia',
    all: 'Todos',
    allSources: 'Todas',
    activeFilters: 'filtro(s) ativo(s)',
    allEvents: 'Mostrando todos os eventos',
    allInboxes: 'Todas as caixas',
    noAssignee: 'Sem responsável',
    noInbox: 'Nenhuma caixa',
    noProduct: 'Nenhum produto',
    connected: 'Google Agenda conectado',
    googleUnavailable: 'Google Agenda não conectado',
    emptyValue: '-',
    more: '+',
    cards: {
      month: 'Eventos no mês',
      today: 'Hoje',
      snoozed: 'Adiados',
      active: 'Ativo',
      inactive: 'Inativo',
    },
  },
  weekdays: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  placeholders: {
    title: 'Ex: Retorno para proposta FGTS',
    contactSearch: 'Nome, telefone ou e-mail',
    location: 'Ex: Loja, escritório ou endereço',
    inventoryQuantity: 'Ex: 1',
    attendeeEmails: 'cliente@email.com, equipe@email.com',
    notes: 'Contexto, pauta ou instruções para o atendimento',
    period: 'Selecione o início e o fim do compromisso',
    periodHelp: 'Selecione a data e o horário de início e fim do compromisso.',
  },
  statuses: {
    scheduled: 'Agendado',
    completed: 'Concluído',
    canceled: 'Cancelado',
    no_show: 'Não compareceu',
  },
  sources: {
    manual: 'Manual',
    snooze: 'Conversa adiada',
    proposal: 'Proposta',
    google: 'Google Agenda',
    external: 'Sistema externo',
    contract: 'Contrato',
    ai: 'Agendado pela IA',
  },
  quoteModal: {
    label: 'Orçamento',
    editar: 'Editar orçamento',
    criarContrato: 'Criar contrato',
    statuses: {
      draft: 'Rascunho',
      sent: 'Enviado',
      approved: 'Aprovado',
      expired: 'Expirado',
      converted: 'Convertido em contrato',
    },
  },
  contractModal: {
    label: 'Contrato',
    modelo: 'Modelo',
    contato: 'Contato',
    dataEntrega: 'Data de entrega',
    dataRetirada: 'Data de retirada',
    formaPagamento: 'Forma de pagamento',
    valorTotal: 'Valor total',
    telefone: 'Telefone',
    endereco: 'Endereço',
    produtos: 'Produtos',
    produto: 'Produto',
    qtd: 'Qtd.',
    valorUnit: 'Valor unit.',
    subtotal: 'Subtotal',
    fechar: 'Fechar',
    verContrato: 'Ver contrato',
    editarContrato: 'Editar contrato',
    verContato: 'Ver contato',
    mapas: 'Rotas',
    abrirGoogleMaps: 'Google Maps',
    abrirWaze: 'Waze',
    enviarRota: 'Enviar rota',
    enviarPara: 'Enviar para',
    buscarContato: 'Buscar contato',
    caixaEntrada: 'Caixa de entrada',
    selecionarCaixaEntrada: 'Selecione a caixa de entrada',
    buscandoContatos: 'Buscando contatos...',
    copiarEndereco: 'Copiar endereço',
    alterarStatus: 'Alterar status',
    confirmar: 'Confirmar',
    cliente: 'Cliente',
    porUnidade: 'por unidade',
    itens: 'itens',
    item: 'item',
  },
  empty: {
    events: 'Nenhum evento nesse período.',
    search: 'Nenhum evento encontrado.',
    contacts: 'Nenhum contato encontrado.',
  },
  messages: {
    loadFailed: 'Não foi possível carregar a agenda.',
    saveFailed: 'Não foi possível salvar o evento.',
    saved: 'Evento salvo.',
    studioReadonly: 'Gerencie este agendamento pelo Talkvia Studio.',
    googleReadonly:
      'Para excluir este evento, acesse o Google Agenda. Esta conexão recebe os eventos do Google.',
    googleIntegrationRequired:
      'Conecte e ative o Google Agenda para criar o link do Google Meet.',
    googleIntegrationLoadFailed:
      'Não foi possível verificar a integração do Google Agenda agora.',
  },
};

const today = ref(new Date());
const currentDate = ref(new Date(today.value));
const events = ref([]);
const contractEvents = ref([]);
const inventoryProducts = ref([]);
const agents = ref([]);
const contactResults = ref([]);
const contactSearch = ref('');
const selectedContact = ref(null);
const isChangingContact = ref(false);
const originalContactId = ref(null);
const editingConversationDisplayId = ref(null);
const integration = ref(null);
const isLoading = ref(true);
const contactConversations = ref([]);
const sendConversationId = ref('');
const conversationsLoading = ref(false);
const conversationLoadError = ref('');
const sendError = ref('');
let conversationRequest = 0;
const sendConversation = computed(() =>
  contactConversations.value.find(
    item => String(item.id) === String(sendConversationId.value)
  )
);
const conversationOptions = computed(() => [
  { value: '', label: 'Selecione a conversa' },
  ...contactConversations.value.map(item => ({
    value: item.id,
    label: `#${item.id} · ${inboxes.value.find(inbox => inbox.id === item.inbox_id)?.name || 'Conversa'}${item.can_reply === false ? ' · Janela de resposta encerrada' : ''}`,
    disabled: item.can_reply === false,
  })),
]);
const isSaving = ref(false);
const eventPeriodValid = ref(true);
const editingEventId = ref(null);
const editingEvent = ref(null);
const deleteDialogRef = ref(null);
const isDeleting = ref(false);
const deleteError = ref('');
const isEventBusy = computed(() => isSaving.value || isDeleting.value);
const eventDialogRef = ref(null);
const showContractModal = ref(false);
const showRouteSendModal = ref(false);
const selectedContract = ref(null);
const selectedContractEventType = ref('');
const selectedContactDetails = ref(null);
const contractStatusChanging = ref(false);
const pendingContractStatus = ref('');
const mapProviderToSend = ref('google');
const mapContactSearch = ref('');
const mapContactResults = ref([]);
const selectedMapContactId = ref('');
const selectedMapInboxId = ref('');
const isSearchingMapContacts = ref(false);
const refreshTimer = ref(null);
const isRefreshing = ref(false);
let calendarMutationVersion = 0;
const isSidePanelOpen = ref(true);
const isSearchOpen = ref(false);
const isViewMenuOpen = ref(false);
const isFiltersOpen = ref(false);
const integrationLoadFailed = ref(false);
const searchQuery = ref('');
const searchInputRef = ref(null);
const searchTimer = ref(null);
// Abre na semana (pedido do usuário); links de evento da conversa continuam abrindo no dia.
const viewMode = ref('week');
const draggedCalendarEvent = ref(null);
const dragOverCalendarKey = ref('');

const viewOptions = [
  { value: 'day', label: copy.actions.day, shortcut: 'D' },
  { value: 'week', label: copy.actions.week, shortcut: 'W' },
  { value: 'month', label: copy.actions.month, shortcut: 'M' },
  { value: 'year', label: copy.actions.year, shortcut: 'Y' },
  { value: 'schedule', label: copy.actions.schedule, shortcut: 'A' },
  { value: 'fourDays', label: copy.actions.fourDays, shortcut: 'X' },
];

const filters = reactive({
  status: '',
  source: '',
  assignee_id: '',
  inbox_id: '',
});
const filterDraft = reactive({ ...filters });

const activeFilterCount = computed(
  () => Object.values(filters).filter(value => String(value).length > 0).length
);

const eventForm = reactive({
  title: '',
  description: '',
  location: '',
  starts_at: '',
  ends_at: '',
  all_day: false,
  status: 'scheduled',
  source: 'manual',
  contact_id: '',
  conversation_id: '',
  inbox_id: '',
  assignee_id: '',
  metadata: {},
});

const loadContactConversations = async () => {
  conversationRequest += 1;
  const request = conversationRequest;
  contactConversations.value = [];
  sendConversationId.value = '';
  conversationLoadError.value = '';
  conversationsLoading.value = false;
  if (!eventForm.contact_id || editingEventId.value) return;
  conversationsLoading.value = true;
  try {
    const { data } = await ContactsAPI.getConversations(eventForm.contact_id);
    if (request !== conversationRequest) return;
    contactConversations.value = (data.payload || []).filter(
      item =>
        !eventForm.inbox_id ||
        String(item.inbox_id) === String(eventForm.inbox_id)
    );
    if (
      contactConversations.value.length === 1 &&
      contactConversations.value[0].can_reply !== false
    )
      sendConversationId.value = contactConversations.value[0].id;
  } catch {
    if (request === conversationRequest)
      conversationLoadError.value =
        'Não foi possível carregar as conversas. Selecione o contato novamente para tentar.';
  } finally {
    if (request === conversationRequest) conversationsLoading.value = false;
  }
};

watch(
  [() => eventForm.contact_id, () => eventForm.inbox_id],
  loadContactConversations
);

const isStudioEvent = computed(() =>
  Boolean(eventForm.metadata?.studio_store_id)
);
const isGoogleReadonly = computed(() =>
  Boolean(
    editingEvent.value?.external_event_id &&
      editingEvent.value?.external_provider === 'google' &&
      integration.value?.sync_direction === 'external_to_talkvia'
  )
);
const canDeleteEvent = computed(() =>
  Boolean(
    editingEventId.value &&
      !isStudioEvent.value &&
      eventForm.source !== 'contract' &&
      !isGoogleReadonly.value
  )
);

const statusOptions = computed(() =>
  Object.entries(copy.statuses).map(([value, label]) => ({ value, label }))
);
const sourceOptions = computed(() =>
  Object.entries(copy.sources).map(([value, label]) => ({ value, label }))
);
const inboxOptions = computed(() => inboxes.value || []);
const assigneeOptions = computed(() => [
  { value: '', label: copy.labels.noAssignee },
  ...agents.value.map(agent => ({
    value: agent.id,
    label: agent?.name || agent?.available_name || agent?.email || '',
  })),
]);
const eventInboxOptions = computed(() => [
  { value: '', label: copy.labels.noInbox },
  ...inboxOptions.value.map(inbox => ({
    value: inbox.id,
    label: getInboxLabel(inbox),
  })),
]);
const productOptions = computed(() => [
  { value: '', label: copy.labels.noProduct },
  ...inventoryProducts.value.map(product => ({
    value: product.id,
    label: product.name,
  })),
]);
const filterStatusOptions = computed(() => [
  { value: '', label: copy.labels.all },
  ...statusOptions.value,
]);
const filterSourceOptions = computed(() => [
  { value: '', label: copy.labels.allSources },
  ...sourceOptions.value,
]);
const filterAssigneeOptions = computed(() => [
  { value: '', label: copy.labels.all },
  ...agents.value.map(agent => ({
    value: agent.id,
    label: agent?.name || agent?.available_name || agent?.email || '',
  })),
]);
const filterInboxOptions = computed(() => [
  { value: '', label: copy.labels.allInboxes },
  ...inboxOptions.value.map(inbox => ({
    value: inbox.id,
    label: getInboxLabel(inbox),
  })),
]);
const isGoogleCalendarActive = computed(() =>
  Boolean(integration.value?.active && integration.value?.has_credentials)
);
const googleMeetLink = computed(
  () => eventForm.metadata?.google_meet_link || ''
);
const showContactSearch = computed(
  () => !selectedContact.value || isChangingContact.value
);
const editingConversationUrl = computed(() =>
  editingConversationDisplayId.value
    ? `/app/accounts/${route.params.accountId}/conversations/${editingConversationDisplayId.value}`
    : ''
);
const contactInitials = contact =>
  String(contact?.name || contact?.phone_number || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
const statusDotClass = status =>
  ({
    scheduled: 'bg-n-blue-9',
    completed: 'bg-n-teal-9',
    canceled: 'bg-n-ruby-9',
    no_show: 'bg-n-amber-9',
  })[status] || 'bg-n-slate-9';
const eventSummary = computed(() => {
  if (!eventForm.starts_at) return '';
  const options = { day: '2-digit', month: 'short' };
  const start = calendarFormat(eventForm.starts_at, options);
  const end = eventForm.ends_at
    ? calendarFormat(eventForm.ends_at, options)
    : '';
  const period = end && end !== start ? `${start} – ${end}` : start;
  if (eventForm.all_day) return `${period} · dia inteiro`;
  const time = value =>
    calendarFormat(value, { hour: '2-digit', minute: '2-digit' });
  return `${period} · ${time(eventForm.starts_at)}${eventForm.ends_at ? `–${time(eventForm.ends_at)}` : ''}`;
});
const monthLabel = computed(() =>
  calendarFormat(currentDate.value, { month: 'long', year: 'numeric' })
);
const weekStart = computed(() =>
  shiftCalendarDays(
    currentDate.value,
    -calendarParts(currentDate.value).weekday
  )
);
const weekEnd = computed(() =>
  calendarDayEnd(shiftCalendarDays(weekStart.value, 6))
);
const dayStart = computed(() => calendarDayStart(currentDate.value));
const dayEnd = computed(() => calendarDayEnd(currentDate.value));
const fourDaysEnd = computed(() =>
  calendarDayEnd(shiftCalendarDays(currentDate.value, 3))
);
const weekRangeLabel = computed(() => {
  const options = { month: 'long', year: 'numeric' };
  if (
    calendarParts(weekStart.value).month === calendarParts(weekEnd.value).month
  )
    return calendarFormat(weekStart.value, options);
  return `${calendarFormat(weekStart.value, { month: 'short' })} - ${calendarFormat(weekEnd.value, options)}`;
});
const rangeLabel = computed(() => {
  const format = value =>
    calendarFormat(value, { day: '2-digit', month: 'short', year: 'numeric' });
  if (viewMode.value === 'day') return format(currentDate.value);
  if (viewMode.value === 'fourDays')
    return `${format(dayStart.value)} - ${format(fourDaysEnd.value)}`;
  if (viewMode.value === 'month') return monthLabel.value;
  if (viewMode.value === 'year')
    return String(calendarParts(currentDate.value).year);
  if (viewMode.value === 'schedule') return 'Programação';
  return weekRangeLabel.value;
});

const currentViewLabel = computed(
  () =>
    viewOptions.find(option => option.value === viewMode.value)?.label ||
    copy.actions.week
);

const monthStart = computed(() => {
  const { year, month } = calendarParts(currentDate.value);
  return calendarDate(year, month);
});
const monthEnd = computed(() => {
  const { year, month } = calendarParts(currentDate.value);
  return calendarDayEnd(calendarDate(year, month + 1, 0));
});
const yearStart = computed(() =>
  calendarDate(calendarParts(currentDate.value).year, 0)
);
const yearEnd = computed(() =>
  calendarDayEnd(calendarDate(calendarParts(currentDate.value).year, 11, 31))
);
const requestRange = computed(() => {
  const ranges = {
    day: { start: dayStart.value, end: dayEnd.value },
    fourDays: { start: dayStart.value, end: fourDaysEnd.value },
    month: { start: monthStart.value, end: monthEnd.value },
    year: { start: yearStart.value, end: yearEnd.value },
    schedule: {
      start: dayStart.value,
      end: calendarDayEnd(shiftCalendarMonths(dayStart.value, 3)),
    },
  };
  return (
    ranges[viewMode.value] || { start: weekStart.value, end: weekEnd.value }
  );
});
const toDateKey = calendarDateKey;
const calendarDays = computed(() => {
  const start = shiftCalendarDays(
    monthStart.value,
    -calendarParts(monthStart.value).weekday
  );
  return Array.from({ length: 42 }, (_, index) => {
    const date = shiftCalendarDays(start, index);
    return {
      date,
      key: toDateKey(date),
      currentMonth:
        calendarParts(date).month === calendarParts(currentDate.value).month,
      today: toDateKey(date) === toDateKey(today.value),
    };
  });
});
const visibleDays = computed(() => {
  const amount = { day: 1, fourDays: 4, week: 7 }[viewMode.value] || 7;
  const start = viewMode.value === 'week' ? weekStart.value : dayStart.value;
  return Array.from({ length: amount }, (_, index) => {
    const date = shiftCalendarDays(start, index);
    const parts = calendarParts(date);
    return {
      date,
      key: toDateKey(date),
      weekday: copy.weekdays[parts.weekday],
      day: parts.day,
      today: toDateKey(date) === toDateKey(today.value),
    };
  });
});
const yearMonths = computed(() =>
  Array.from({ length: 12 }, (_, index) => {
    const date = calendarDate(calendarParts(currentDate.value).year, index);
    return {
      date,
      key: toDateKey(date).slice(0, 7),
      label: calendarFormat(date, { month: 'short' }),
    };
  })
);

const timeSlots = Array.from({ length: 24 }, (_, hour) => hour);

const allCalendarItems = computed(() => {
  const showContracts = filters.source === '' || filters.source === 'contract';
  return [...events.value, ...(showContracts ? contractEvents.value : [])];
});

const visibleCalendarItems = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return allCalendarItems.value;

  return allCalendarItems.value.filter(event =>
    [
      event.title,
      event.description,
      event.location,
      copy.sources[event.source],
      event.contact?.name,
      event.contact?.phone_number,
      event.contractData?.recipient_name,
      event.contractData?.template_name,
    ]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(query))
  );
});

const upcomingEvents = computed(() =>
  [...visibleCalendarItems.value]
    .filter(event => calendarInstant(event.starts_at) >= new Date())
    .sort((a, b) => calendarInstant(a.starts_at) - calendarInstant(b.starts_at))
    .slice(0, 8)
);

const scheduleEvents = computed(() =>
  [...visibleCalendarItems.value].sort(
    (a, b) => calendarInstant(a.starts_at) - calendarInstant(b.starts_at)
  )
);

const searchResults = computed(() => {
  if (searchQuery.value.trim().length < 2) return [];
  return scheduleEvents.value.slice(0, 8);
});

const eventsForDay = date => {
  const key = toDateKey(date);
  return visibleCalendarItems.value.filter(
    event =>
      toDateKey(event.starts_at) <= key &&
      toDateKey(event.ends_at || event.starts_at) >= key
  );
};

const calendarWeeks = computed(() => {
  const result = [];
  for (let i = 0; i < calendarDays.value.length; i += 7) {
    result.push(calendarDays.value.slice(i, i + 7));
  }
  return result;
});

const isMultiDayEvent = event => {
  if (!event.starts_at || !event.ends_at) return false;
  return toDateKey(event.starts_at) !== toDateKey(event.ends_at);
};

const getWeekMultiDayLayout = week => {
  const weekStartKey = week[0].key;
  const weekEndKey = week[6].key;

  const multiDayEvents = visibleCalendarItems.value
    .filter(event => {
      if (!isMultiDayEvent(event)) return false;

      const startKey = toDateKey(event.starts_at);
      const endKey = toDateKey(event.ends_at);
      return startKey <= weekEndKey && endKey >= weekStartKey;
    })
    .sort((a, b) => {
      const startSort = a.starts_at.localeCompare(b.starts_at);
      if (startSort !== 0) return startSort;
      return b.ends_at.localeCompare(a.ends_at);
    });

  const rowEnds = [];

  return multiDayEvents.map(event => {
    const startKey = toDateKey(event.starts_at);
    const endKey = toDateKey(event.ends_at);
    const startCol =
      startKey < weekStartKey ? 0 : week.findIndex(day => day.key === startKey);
    const endCol =
      endKey > weekEndKey ? 6 : week.findIndex(day => day.key === endKey);
    const resolvedStartCol = startCol === -1 ? 0 : startCol;
    const resolvedEndCol = endCol === -1 ? 6 : endCol;

    let row = 0;
    while (rowEnds[row] !== undefined && rowEnds[row] >= resolvedStartCol) {
      row += 1;
    }
    rowEnds[row] = resolvedEndCol;

    return {
      event,
      row,
      startCol: resolvedStartCol,
      colSpan: resolvedEndCol - resolvedStartCol + 1,
      startsThisWeek: startKey >= weekStartKey,
      endsThisWeek: endKey <= weekEndKey,
    };
  });
};

const weekLayouts = computed(() =>
  calendarWeeks.value.map(week => getWeekMultiDayLayout(week))
);

const weekMultiDayRows = weekIndex => {
  const layout = weekLayouts.value[weekIndex] || [];
  if (!layout.length) return 0;
  return Math.max(...layout.map(item => item.row)) + 1;
};

const singleDayEventsForDate = date => {
  const key = toDateKey(date);
  return visibleCalendarItems.value
    .filter(
      event => toDateKey(event.starts_at) === key && !isMultiDayEvent(event)
    )
    .sort(
      (a, b) => calendarInstant(a.starts_at) - calendarInstant(b.starts_at)
    );
};

const monthEventsForDate = date => singleDayEventsForDate(date);
// Evento de dia inteiro é anotação do dia (ex.: "8 jogos"), não compromisso com horário.
const monthDayNotes = date => monthEventsForDate(date).filter(isDayNote);
const monthTimedEvents = date =>
  monthEventsForDate(date).filter(event => !isDayNote(event));
const monthVisibleTimedEvents = date =>
  monthTimedEvents(date).slice(
    0,
    Math.max(4 - monthDayNotes(date).slice(0, 2).length, 0)
  );
const monthDayTotal = date =>
  formatDayTotals(dayQuantityTotals(monthEventsForDate(date)));

const hiddenMonthEventsCount = date =>
  Math.max(
    monthEventsForDate(date).length -
      monthDayNotes(date).slice(0, 2).length -
      monthVisibleTimedEvents(date).length,
    0
  );

const eventDateLabel = value =>
  value ? calendarFormat(value, { day: '2-digit', month: '2-digit' }) : '';
const multiDayEventLabel = event =>
  `${eventDateLabel(event.starts_at)} até ${eventDateLabel(event.ends_at)}`;
const eventsForHour = (date, hour) =>
  eventsForDay(date)
    .filter(
      event =>
        (event.all_day || toDateKey(event.starts_at) < toDateKey(date)
          ? 0
          : calendarParts(event.starts_at).hour) === hour
    )
    .sort(
      (a, b) => calendarInstant(a.starts_at) - calendarInstant(b.starts_at)
    );
const eventsForMonth = date =>
  visibleCalendarItems.value.filter(
    event =>
      toDateKey(event.starts_at).slice(0, 7) === toDateKey(date).slice(0, 7)
  );
const formatDateTime = value =>
  value
    ? calendarFormat(value, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';
const formatTime = value =>
  value ? calendarFormat(value, { hour: '2-digit', minute: '2-digit' }) : '';
const formatHour = hour =>
  hour === 0 ? 'GMT-03' : `${String(hour).padStart(2, '0')}:00`;
const dateInputValue = calendarISO;
const calendarDropKey = (date, hour = null) =>
  hour === null ? toDateKey(date) : `${toDateKey(date)}-${hour}`;

const isDraggableEvent = event =>
  !isEventBusy.value &&
  !event?.metadata?.studio_store_id &&
  !event?.quote &&
  (event?.source !== 'contract' || Boolean(event?.contractData?.id));

const clearCalendarDragState = () => {
  draggedCalendarEvent.value = null;
  dragOverCalendarKey.value = '';
};

const startCalendarEventDrag = (event, dragEvent) => {
  if (!isDraggableEvent(event)) {
    dragEvent.preventDefault();
    return;
  }

  draggedCalendarEvent.value = event;
  dragEvent.dataTransfer.effectAllowed = 'move';
  dragEvent.dataTransfer.setData('text/plain', String(event.id));
};

const handleCalendarEventDragOver = (date, dragEvent, hour = null) => {
  if (!draggedCalendarEvent.value) return;
  dragEvent.preventDefault();
  dragEvent.dataTransfer.dropEffect = 'move';
  dragOverCalendarKey.value = calendarDropKey(date, hour);
};

const handleCalendarEventDragLeave = (date, hour = null) => {
  if (dragOverCalendarKey.value === calendarDropKey(date, hour)) {
    dragOverCalendarKey.value = '';
  }
};

const updateLocalCalendarEvent = updatedEvent => {
  calendarMutationVersion += 1;
  const index = events.value.findIndex(
    event => String(event.id) === String(updatedEvent.id)
  );
  if (index !== -1) {
    events.value[index] = { ...events.value[index], ...updatedEvent };
  } else {
    events.value.push(updatedEvent);
  }
};

const updateLocalContractCalendarEvent = (event, payload) => {
  const contractId = event.contractData?.id;
  if (!contractId) return;

  const updatedContract = { ...event.contractData, ...payload };
  contractEvents.value = contractEvents.value.map(item =>
    item.contractData?.id === contractId
      ? {
          ...item,
          starts_at: payload.scheduled_at || item.starts_at,
          ends_at: payload.pickup_at || payload.scheduled_at || item.ends_at,
          contractData: updatedContract,
        }
      : item
  );

  if (selectedContract.value?.id === contractId) {
    selectedContract.value = { ...selectedContract.value, ...payload };
  }
};

const moveContractCalendarEvent = async (event, movedStart, originalStart) => {
  const originalEnd = event.ends_at ? calendarInstant(event.ends_at) : null;
  const payload = {};

  if (event.contractEventType === 'pickup') {
    payload.pickup_at = dateInputValue(movedStart);
  } else {
    const duration = originalEnd
      ? originalEnd.getTime() - originalStart.getTime()
      : 0;
    const movedEnd = originalEnd
      ? new Date(movedStart.getTime() + duration)
      : null;
    payload.scheduled_at = dateInputValue(movedStart);
    payload.pickup_at = movedEnd ? dateInputValue(movedEnd) : null;
  }

  await ContractsAPI.update(event.contractData.id, payload);
  updateLocalContractCalendarEvent(event, payload);
};

const isPastSchedule = event =>
  event.all_day
    ? calendarDateKey(event.starts_at) < calendarDateKey(new Date())
    : calendarInstant(event.starts_at) < new Date();

const moveCalendarEventToDate = async (targetDate, dragEvent, hour = null) => {
  dragEvent.preventDefault();
  dragEvent.stopPropagation();

  const event = draggedCalendarEvent.value;
  clearCalendarDragState();
  if (!event || !isDraggableEvent(event) || !event.starts_at) return;

  const originalStart = calendarInstant(event.starts_at);
  const movedPeriod = moveCalendarPeriod(event, targetDate, hour);
  if (isPastSchedule({ ...event, ...movedPeriod })) {
    useAlert('Escolha uma data e um horário de hoje em diante.', {
      variant: 'warning',
    });
    return;
  }
  const movedStart = calendarInstant(movedPeriod.starts_at);
  const movedEnd = movedPeriod.ends_at
    ? calendarInstant(movedPeriod.ends_at)
    : null;
  if (movedStart.getTime() === originalStart.getTime()) return;

  isSaving.value = true;
  try {
    if (event.source === 'contract') {
      await moveContractCalendarEvent(event, movedStart, originalStart);
    } else {
      const payload = {
        starts_at: dateInputValue(movedStart),
        ends_at: movedEnd ? dateInputValue(movedEnd) : '',
      };
      const { data } = await TalkviaCalendarEventsAPI.update(event.id, payload);
      updateLocalCalendarEvent(
        data || { ...event, ...payload, ends_at: payload.ends_at || null }
      );
    }
    useAlert(copy.messages.saved);
  } catch {
    useAlert(copy.messages.saveFailed);
  } finally {
    isSaving.value = false;
  }
};
const eventSourceLabel = value => copy.sources[value] || value || '-';

const contractEventTypeLabel = value =>
  ({
    delivery: copy.contractModal.dataEntrega,
    pickup: copy.contractModal.dataRetirada,
  })[value] || copy.contractModal.label;

const contractStatusLabel = status =>
  ({
    pending: 'Pendente',
    signed: 'Assinado',
    expired: 'Expirado',
    cancelled: 'Cancelado',
    awaiting_delivery: 'Aguardando entrega',
    completed: 'Concluído',
  })[status] || status;

const contractStatusColor = status =>
  ({
    pending: 'border-n-amber-5 bg-n-amber-3 text-n-amber-11',
    signed: 'border-n-teal-5 bg-n-teal-3 text-n-teal-11',
    expired: 'border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11',
    cancelled: 'border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11',
    awaiting_delivery: 'border-n-blue-5 bg-n-blue-3 text-n-blue-11',
    completed: 'border-n-green-5 bg-n-green-3 text-n-green-11',
    not_delivered: 'border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11',
  })[status] || 'border-n-slate-5 bg-n-slate-3 text-n-slate-11';

const calendarStatusColor = status =>
  ({
    scheduled: 'bg-n-blue-3 text-n-blue-11',
    completed: 'bg-n-green-3 text-n-green-11',
    canceled: 'bg-n-ruby-3 text-n-ruby-11',
    no_show: 'bg-n-amber-3 text-n-amber-11',
  })[status] || 'bg-n-alpha-2 text-n-slate-12';

const contractMetadataAddress = contract => {
  const address = contract?.metadata?.recipient_address || {};
  const street = [address.address, address.number, address.complement]
    .filter(Boolean)
    .join(', ');
  const cityState = [address.city, address.state].filter(Boolean).join(' - ');

  return [street, address.neighborhood, cityState, address.zip_code]
    .filter(Boolean)
    .join(', ');
};

const selectedContactAddress = computed(() => {
  const contractAddress = contractMetadataAddress(selectedContract.value);
  if (contractAddress) return contractAddress;

  const attrs = selectedContactDetails.value?.additional_attributes;
  if (!attrs) return '';
  const {
    addressLine,
    addressNumber,
    addressComplement,
    neighborhood,
    city,
    state: uf,
    zipCode,
  } = attrs;
  const parts = [
    [addressLine, addressNumber, addressComplement].filter(Boolean).join(', '),
    neighborhood,
    [city, uf].filter(Boolean).join(' - '),
    zipCode,
  ].filter(Boolean);
  return parts.join(', ');
});

const mapUrl = provider => {
  if (!selectedContactAddress.value) return '';
  const query = encodeURIComponent(selectedContactAddress.value);
  if (provider === 'waze') return `https://waze.com/ul?q=${query}&navigate=yes`;
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
};

const selectMapContact = contact => {
  selectedMapContactId.value = contact.id;
  mapContactSearch.value =
    `${contact.name || 'Contato'} ${contact.phone_number || ''}`.trim();
  mapContactResults.value = [];
};

const searchMapContacts = async () => {
  if (mapContactSearch.value.trim().length < 2) {
    mapContactResults.value = [];
    return;
  }

  isSearchingMapContacts.value = true;
  try {
    const { data } = await ContactsAPI.search(mapContactSearch.value, 1);
    mapContactResults.value = data.payload || [];
  } finally {
    isSearchingMapContacts.value = false;
  }
};

const handleMapContactSearch = () => {
  selectedMapContactId.value = '';
  searchMapContacts();
};

const sendMapToContact = async () => {
  if (!selectedContract.value?.id || !selectedContactAddress.value) return;

  try {
    await ContractsAPI.sendMapLink(selectedContract.value.id, {
      provider: mapProviderToSend.value,
      address: selectedContactAddress.value,
      contact_id: selectedMapContactId.value || null,
      inbox_id: selectedMapInboxId.value || null,
    });
    useAlert('Rota enviada na conversa.');
    showRouteSendModal.value = false;
  } catch {
    useAlert('Não foi possível enviar a rota para o contato.', {
      variant: 'error',
    });
  }
};

const mapProviderOptions = [
  { value: 'google', label: copy.contractModal.abrirGoogleMaps },
  { value: 'waze', label: copy.contractModal.abrirWaze },
];

const sendMapDisabled = computed(
  () =>
    !selectedContract.value?.id ||
    !selectedContactAddress.value ||
    !selectedMapContactId.value ||
    !selectedMapInboxId.value
);

const openRouteSendModal = () => {
  showRouteSendModal.value = true;
};

const closeRouteSendModal = () => {
  showRouteSendModal.value = false;
  mapContactResults.value = [];
};

const copyContractAddress = async () => {
  if (!selectedContactAddress.value) return;
  try {
    await navigator.clipboard.writeText(selectedContactAddress.value);
    useAlert('Endereço copiado.');
  } catch {
    useAlert('Não foi possível copiar o endereço.', { variant: 'error' });
  }
};

const openContractModal = async event => {
  selectedContract.value = event.contractData;
  selectedContractEventType.value = event.contractEventType || '';
  selectedContactDetails.value = null;
  pendingContractStatus.value = event.contractData?.status || '';
  selectedMapContactId.value = event.contractData?.contact_id || '';
  selectedMapInboxId.value = '';
  mapContactSearch.value =
    event.contractData?.contact_name ||
    event.contractData?.recipient_name ||
    '';
  mapContactResults.value = [];
  showContractModal.value = true;
  if (event.contractData?.contact_id) {
    try {
      const { data } = await ContactsAPI.show(event.contractData.contact_id);
      selectedContactDetails.value = data;
    } catch {
      // ignore
    }
  }
};

const goToContact = contactId => {
  showContractModal.value = false;
  router.push({
    name: 'contacts_edit',
    params: { accountId: route.params.accountId, contactId },
  });
};

// O orçamento aproveita o modal do contrato: mesmos dados de entrega, outra cara
// e ações próprias (editar o orçamento ou criar o contrato).
const quoteAsContract = event => {
  const quote = event.quote;
  return {
    quote_id: quote.id,
    status: quote.status,
    recipient_name: quote.recipient_name || quote.contact_name || '',
    recipient_phone: quote.recipient_phone,
    contact_id: quote.contact_id,
    contact_name: quote.contact_name,
    scheduled_at: quote.scheduled_at || event.starts_at,
    pickup_at: quote.pickup_at,
    payment_method: quote.payment_method,
    contract_id: quote.contract_id,
    metadata: {
      invoice_total: quote.invoice_total,
      invoice_items: quote.invoice_items || [],
      recipient_address: quote.recipient_address || {},
    },
  };
};
const isQuoteSelected = computed(() =>
  Boolean(selectedContract.value?.quote_id)
);
const goToQuote = action => {
  const quoteId = selectedContract.value?.quote_id;
  if (!quoteId) return;
  showContractModal.value = false;
  router.push({
    name: 'talkvia_contracts',
    query:
      action === 'convert'
        ? { convert_quote_id: quoteId }
        : { section: 'quotes', edit_quote_id: quoteId },
  });
};

const goToContractEdit = () => {
  if (!selectedContract.value?.id) return;
  showContractModal.value = false;
  router.push({
    name: 'talkvia_contracts',
    query: { edit_contract_id: selectedContract.value.id },
  });
};

// Orçamentos agendados usam outra cor e borda tracejada para não se confundirem
// com contratos (mesmo layout, outra cara).
const QUOTE_EVENT_COLOR =
  'border border-dashed border-n-iris-6 bg-n-iris-3 text-n-iris-11';
const eventColor = event => {
  if (event.quote) return QUOTE_EVENT_COLOR;
  if (event.source === 'contract') {
    return contractStatusColor(event.contractData?.status);
  }

  return calendarStatusColor(event.status);
};

const requestParams = () =>
  Object.fromEntries(
    Object.entries({
      ...filters,
      q: searchQuery.value.trim().length >= 2 ? searchQuery.value.trim() : '',
      start_at: requestRange.value.start.toISOString(),
      end_at: requestRange.value.end.toISOString(),
    }).filter(([, value]) => String(value || '').trim())
  );

const resetForm = (date = new Date(), hour = 9) => {
  sendError.value = '';
  conversationRequest += 1;
  contactConversations.value = [];
  sendConversationId.value = '';
  eventPeriodValid.value = true;
  editingEventId.value = null;
  editingEvent.value = null;
  deleteError.value = '';
  contactSearch.value = '';
  contactResults.value = [];
  selectedContact.value = null;
  isChangingContact.value = false;
  originalContactId.value = null;
  editingConversationDisplayId.value = null;
  let startsAt = calendarAt(
    toDateKey(date),
    `${String(hour).padStart(2, '0')}:00`
  );
  const nextSlot = new Date(Math.ceil((Date.now() + 60000) / 900000) * 900000);
  if (startsAt < nextSlot) startsAt = nextSlot;
  const endsAt = new Date(startsAt.getTime() + 1800000);
  Object.assign(eventForm, {
    title: '',
    description: '',
    location: '',
    starts_at: dateInputValue(startsAt),
    ends_at: dateInputValue(endsAt),
    all_day: false,
    status: 'scheduled',
    source: 'manual',
    contact_id: '',
    conversation_id: '',
    inbox_id: '',
    assignee_id: '',
    metadata: {
      inventory_product_id: '',
      inventory_quantity: '',
      create_google_meet: false,
      attendee_emails: '',
    },
  });
};

const openEventDialog = date => {
  if (isEventBusy.value) return;
  resetForm(date);
  eventDialogRef.value?.open();
};

const openEventDialogAt = (date, hour = 9) => {
  if (isEventBusy.value) return;
  resetForm(date, hour);
  eventDialogRef.value?.open();
};

const editEvent = event => {
  sendError.value = '';
  if (isEventBusy.value) return;
  eventPeriodValid.value = true;
  editingEventId.value = event.id;
  editingEvent.value = event;
  deleteError.value = '';
  // O contato escolhido aparece como cartão; a lista de busca só abre ao trocar.
  contactResults.value = [];
  contactSearch.value = '';
  selectedContact.value = event.contact || null;
  isChangingContact.value = false;
  originalContactId.value = event.contact_id || null;
  editingConversationDisplayId.value = event.conversation_display_id || null;
  Object.assign(eventForm, {
    title: event.title,
    description: event.description || '',
    location: event.location || '',
    starts_at: event.starts_at ? calendarISO(event.starts_at) : '',
    ends_at: event.ends_at ? calendarISO(event.ends_at) : '',
    all_day: event.all_day,
    status: event.status,
    source: event.source,
    contact_id: event.contact_id || '',
    conversation_id: event.conversation_id || '',
    inbox_id: event.inbox_id || '',
    assignee_id: event.assignee_id || '',
    metadata: {
      ...event.metadata,
      inventory_product_id: event.metadata?.inventory_product_id || '',
      inventory_quantity: event.metadata?.inventory_quantity || '',
      create_google_meet: Boolean(
        event.metadata?.create_google_meet || event.metadata?.google_meet_link
      ),
      attendee_emails: Array.isArray(event.metadata?.attendee_emails)
        ? event.metadata.attendee_emails.join(', ')
        : event.metadata?.attendee_emails || '',
    },
  });
  eventDialogRef.value?.open();
};
const openCalendarItem = event => {
  if (event?.quote)
    return openContractModal({ contractData: quoteAsContract(event) });
  if (event?.source === 'contract') return openContractModal(event);
  return editEvent(event);
};

const loadData = async ({ silent = false } = {}) => {
  if (isRefreshing.value) return;

  isRefreshing.value = true;
  const requestedVersion = calendarMutationVersion;
  if (!silent) {
    isLoading.value = true;
  }
  try {
    const [
      eventsResponse,
      integrationResponse,
      agentsResponse,
      productsResponse,
      contractsResponse,
    ] = await Promise.all([
      TalkviaCalendarEventsAPI.get(requestParams()),
      TalkviaCalendarIntegrationAPI.get().catch(() => {
        integrationLoadFailed.value = true;
        if (!silent) {
          useAlert(copy.messages.googleIntegrationLoadFailed, {
            variant: 'warning',
          });
        }
        return { data: null };
      }),
      AgentsAPI.get(),
      TalkviaInventoryProductsAPI.get({ active: true }),
      ContractsAPI.get({
        has_scheduled_at: true,
        start_at: requestRange.value.start.toISOString(),
        end_at: requestRange.value.end.toISOString(),
      }),
      store.dispatch('inboxes/get'),
    ]);
    if (requestedVersion !== calendarMutationVersion) return;
    events.value = eventsResponse.data || [];
    integration.value = integrationResponse.data || null;
    if (integrationResponse.data) integrationLoadFailed.value = false;
    agents.value = agentsResponse.data || [];
    inventoryProducts.value = productsResponse.data?.products || [];
    contractEvents.value = buildContractCalendarEvents(
      contractsResponse.data?.contracts || []
    );
  } catch {
    if (!silent) {
      useAlert(copy.messages.loadFailed);
    }
  } finally {
    isRefreshing.value = false;
    if (!silent) {
      isLoading.value = false;
    }
  }
};

const clearFilters = () => {
  const emptyFilters = {
    status: '',
    source: '',
    assignee_id: '',
    inbox_id: '',
  };
  Object.assign(filters, emptyFilters);
  Object.assign(filterDraft, emptyFilters);
  isFiltersOpen.value = false;
  loadData();
};

const applyFilters = () => {
  Object.assign(filters, filterDraft);
  isFiltersOpen.value = false;
  loadData();
};

const toggleFilters = () => {
  if (!isFiltersOpen.value) Object.assign(filterDraft, filters);
  isFiltersOpen.value = !isFiltersOpen.value;
};

const closeFilters = () => {
  isFiltersOpen.value = false;
};

const updateEventPeriod = period => {
  if (isEventBusy.value || isStudioEvent.value) return;
  Object.assign(eventForm, period);
};

const openDeleteDialog = () => {
  if (isEventBusy.value || !canDeleteEvent.value) return;
  deleteError.value = '';
  deleteDialogRef.value?.open();
};
const deleteEvent = async () => {
  if (isEventBusy.value || !canDeleteEvent.value) return;
  const id = editingEventId.value;
  isDeleting.value = true;
  deleteError.value = '';
  try {
    await TalkviaCalendarEventsAPI.delete(id);
    calendarMutationVersion += 1;
    events.value = events.value.filter(
      event => String(event.id) !== String(id)
    );
    deleteDialogRef.value?.close();
    eventDialogRef.value?.close();
    useAlert('Evento excluído.');
  } catch (error) {
    deleteError.value =
      error?.response?.data?.error ||
      'Não foi possível excluir o evento. Tente novamente.';
  } finally {
    isDeleting.value = false;
  }
};

const handleGoogleMeetToggle = () => {
  if (eventForm.metadata?.create_google_meet && !isGoogleCalendarActive.value) {
    useAlert(copy.messages.googleIntegrationRequired, { variant: 'warning' });
  }
};

const openGoogleCalendarSettings = () => {
  eventDialogRef.value?.close();
  router.push({ name: 'settings_integrations_google_calendar' });
};

const refreshCalendarSilently = () => {
  if (document.hidden) return;
  today.value = new Date();
  loadData({ silent: true });
};

const formatContractMoney = value =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
const selectedContractItems = computed(
  () => selectedContract.value?.metadata?.invoice_items || []
);
const contractItemsSummary = computed(() => {
  const quantity = selectedContractItems.value.reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0
  );
  const label =
    quantity === 1 ? copy.contractModal.item : copy.contractModal.itens;
  return `${quantity.toLocaleString('pt-BR')} ${label}`;
});

const CONTRACT_STATUSES = [
  { value: 'pending', label: 'Pendente' },
  { value: 'signed', label: 'Assinado' },
  { value: 'awaiting_delivery', label: 'Aguardando entrega' },
  { value: 'completed', label: 'Concluído' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'expired', label: 'Expirado' },
];

const saveContractStatus = async () => {
  if (
    !selectedContract.value ||
    pendingContractStatus.value === selectedContract.value.status
  )
    return;
  contractStatusChanging.value = true;
  try {
    await ContractsAPI.updateStatus(
      selectedContract.value.id,
      pendingContractStatus.value
    );
    selectedContract.value = {
      ...selectedContract.value,
      status: pendingContractStatus.value,
    };
    useAlert('Status atualizado.');
    await loadData({ silent: true });
  } catch {
    useAlert('Não foi possível atualizar o status.', { variant: 'error' });
  } finally {
    contractStatusChanging.value = false;
  }
};

const refreshCalendar = () => {
  loadData();
};

const toggleSidePanel = () => {
  isSidePanelOpen.value = !isSidePanelOpen.value;
};

const openSearch = async () => {
  isSearchOpen.value = true;
  await nextTick();
  searchInputRef.value?.focus();
};

const closeSearch = () => {
  isSearchOpen.value = false;
  searchQuery.value = '';
  loadData({ silent: true });
};

const toggleSearch = () => {
  if (isSearchOpen.value) {
    closeSearch();
    return;
  }

  openSearch();
};

const closeViewMenu = () => {
  isViewMenuOpen.value = false;
};

const selectViewMode = mode => {
  viewMode.value = mode;
  closeViewMenu();
  loadData();
};

const selectDate = date => {
  currentDate.value = new Date(date);
  loadData();
};

const moveCalendar = step => {
  const mode = viewMode.value;
  if (mode === 'month' || mode === 'schedule')
    currentDate.value = shiftCalendarMonths(
      currentDate.value,
      step,
      mode === 'month'
    );
  else if (mode === 'year')
    currentDate.value = shiftCalendarMonths(currentDate.value, step * 12);
  else
    currentDate.value = shiftCalendarDays(
      currentDate.value,
      step * ({ day: 1, fourDays: 4 }[mode] || 7)
    );
  loadData();
};

const clearSearch = () => {
  searchQuery.value = '';
  searchInputRef.value?.focus();
  loadData({ silent: true });
};

watch(searchQuery, value => {
  if (searchTimer.value) window.clearTimeout(searchTimer.value);
  searchTimer.value = window.setTimeout(() => {
    if (!isSearchOpen.value) return;
    if (value.trim().length === 1) return;
    loadData({ silent: true });
  }, 350);
});

const startAutoRefresh = () => {
  if (refreshTimer.value) return;
  refreshTimer.value = window.setInterval(
    refreshCalendarSilently,
    AUTO_REFRESH_INTERVAL_MS
  );
};

const stopAutoRefresh = () => {
  if (!refreshTimer.value) return;
  window.clearInterval(refreshTimer.value);
  refreshTimer.value = null;
};

const handleVisibilityChange = () => {
  if (document.hidden) return;
  refreshCalendarSilently();
};

const saveEvent = async (send = false) => {
  if (
    isEventBusy.value ||
    isStudioEvent.value ||
    !eventPeriodValid.value ||
    !eventForm.title.trim()
  )
    return;
  if (
    !calendarISO(eventForm.starts_at) ||
    !calendarISO(eventForm.ends_at) ||
    calendarInstant(eventForm.ends_at) <= calendarInstant(eventForm.starts_at)
  ) {
    useAlert('Selecione um período válido, com o fim após o início.', {
      variant: 'warning',
    });
    return;
  }

  const changedStart =
    !editingEvent.value ||
    calendarISO(eventForm.starts_at) !==
      calendarISO(editingEvent.value.starts_at) ||
    eventForm.all_day !== editingEvent.value.all_day;
  if (changedStart && isPastSchedule(eventForm)) {
    useAlert('Escolha uma data e um horário de hoje em diante.', {
      variant: 'warning',
    });
    return;
  }
  if (
    send &&
    (!sendConversation.value ||
      sendConversation.value.can_reply === false ||
      eventForm.status !== 'scheduled')
  )
    return;

  if (eventForm.metadata?.create_google_meet && !isGoogleCalendarActive.value) {
    useAlert(copy.messages.googleIntegrationRequired, { variant: 'warning' });
    return;
  }

  isSaving.value = true;
  const targetConversationId = sendConversationId.value;
  try {
    const payload = {
      ...eventForm,
      ...(send ? { conversation_display_id: targetConversationId } : {}),
      starts_at: calendarISO(eventForm.starts_at),
      ends_at: calendarISO(eventForm.ends_at),
      contact_id: eventForm.contact_id || null,
      conversation_id: eventForm.conversation_id || null,
      inbox_id: eventForm.inbox_id || null,
      assignee_id: eventForm.assignee_id || null,
      metadata: {
        ...eventForm.metadata,
        inventory_product_id: eventForm.metadata?.inventory_product_id || '',
        inventory_quantity: eventForm.metadata?.inventory_quantity || '',
        create_google_meet: Boolean(eventForm.metadata?.create_google_meet),
        attendee_emails: eventForm.metadata?.attendee_emails || '',
        sync_google: Boolean(integration.value?.active),
      },
    };
    if (editingEventId.value) {
      const { data } = await TalkviaCalendarEventsAPI.update(
        editingEventId.value,
        payload
      );
      if (data) updateLocalCalendarEvent(data);
    } else {
      const { data } = await TalkviaCalendarEventsAPI.create(payload);
      if (send) {
        editingEventId.value = data.id;
        editingEvent.value = data;
        editingConversationDisplayId.value = targetConversationId;
        Object.assign(eventForm, {
          conversation_id: data.conversation_id,
          inbox_id: data.inbox_id,
          metadata: data.metadata,
        });
        updateLocalCalendarEvent(data);
        try {
          await MessageAPI.create({
            conversationId: targetConversationId,
            message: appointmentMessage(data),
            private: false,
          });
        } catch {
          sendError.value =
            'Evento criado, mas não foi possível confirmar o envio. Confira a conversa antes de tentar enviar novamente.';
          useAlert(sendError.value, { variant: 'warning' });
          return;
        }
      }
    }
    useAlert(
      send
        ? 'Evento criado e agendamento encaminhado para envio na conversa.'
        : copy.messages.saved
    );
    eventDialogRef.value?.close();
    await loadData();
  } catch {
    useAlert(copy.messages.saveFailed);
  } finally {
    isSaving.value = false;
  }
};

const searchContacts = async () => {
  if (!contactSearch.value || contactSearch.value.length < 2) {
    contactResults.value = [];
    return;
  }
  try {
    const { data } = await ContactsAPI.search(contactSearch.value, 1);
    contactResults.value = data.payload || [];
  } catch {
    contactResults.value = [];
  }
};

const selectContact = contact => {
  eventForm.contact_id = contact.id;
  selectedContact.value = contact;
  contactSearch.value = '';
  contactResults.value = [];
  isChangingContact.value = false;
  // A conversa vinculada pertence ao contato original; trocar o contato a desfaz.
  if (contact.id !== originalContactId.value) {
    eventForm.conversation_id = '';
    editingConversationDisplayId.value = null;
  }
};

const startChangingContact = () => {
  isChangingContact.value = true;
  contactSearch.value = '';
  contactResults.value = [];
};

const cancelChangingContact = () => {
  isChangingContact.value = false;
  contactSearch.value = '';
  contactResults.value = [];
};

const clearContact = () => {
  eventForm.contact_id = '';
  eventForm.conversation_id = '';
  selectedContact.value = null;
  editingConversationDisplayId.value = null;
  cancelChangingContact();
};

const moveMonth = step => {
  currentDate.value = shiftCalendarMonths(currentDate.value, step, true);
  loadData();
};

const goToday = () => {
  today.value = new Date();
  currentDate.value = new Date(today.value);
  loadData();
};

// Links da Agenda do dia (lateral da conversa): ?date=aaaa-mm-dd&event_id=...
const openFromRouteQuery = async () => {
  const date = String(route.query?.date || '');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;

  currentDate.value = calendarAt(date, '12:00:00.000');
  viewMode.value = 'day';
  await loadData();
  const eventId = String(route.query?.event_id || '');
  const event = eventId
    ? allCalendarItems.value.find(item => String(item.id) === eventId)
    : null;
  if (event) openCalendarItem(event);
  return true;
};

onMounted(async () => {
  startAutoRefresh();
  if (!(await openFromRouteQuery())) loadData();
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onUnmounted(() => {
  stopAutoRefresh();
  if (searchTimer.value) window.clearTimeout(searchTimer.value);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>

<template>
  <section
    class="relative flex h-full w-full flex-col bg-n-solid-1 text-n-slate-12"
    :aria-busy="isLoading"
  >
    <div
      v-if="isLoading"
      class="absolute inset-0 z-[100] flex items-center justify-center gap-3 bg-n-solid-1/95"
      role="status"
      aria-live="polite"
      data-testid="calendar-loading"
    >
      <Spinner
        :size="24"
        :show-message="false"
        class="shrink-0 text-n-brand"
        aria-hidden="true"
      />
      <span>{{ copy.actions.loadingEvents }}</span>
    </div>
    <header
      class="relative z-50 flex min-h-[4.5rem] shrink-0 items-center gap-3 overflow-visible border-b border-n-weak bg-n-solid-1 px-4 py-3"
    >
      <button
        type="button"
        class="grid size-10 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
        :class="isSidePanelOpen && 'bg-n-alpha-2 text-n-brand'"
        :aria-label="copy.actions.menu"
        @click="toggleSidePanel"
      >
        <span class="i-lucide-menu size-5" />
      </button>
      <span
        class="grid size-10 place-content-center rounded-xl bg-n-blue-9 text-lg font-bold text-white shadow-sm"
      >
        {{ calendarParts(today).day }}
      </span>
      <h1 class="mb-0 mr-4 shrink-0 text-2xl font-normal text-n-slate-12">
        {{ copy.title }}
      </h1>

      <button
        type="button"
        class="h-10 rounded-full border border-n-strong px-5 text-sm font-semibold text-n-slate-12 hover:bg-n-alpha-2"
        @click="goToday"
      >
        {{ copy.actions.today }}
      </button>
      <div class="flex items-center gap-1">
        <button
          type="button"
          class="grid size-9 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
          :title="copy.actions.previous"
          @click="moveCalendar(-1)"
        >
          <span class="i-lucide-chevron-left size-5" />
        </button>
        <button
          type="button"
          class="grid size-9 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
          :title="copy.actions.next"
          @click="moveCalendar(1)"
        >
          <span class="i-lucide-chevron-right size-5" />
        </button>
      </div>
      <h2
        class="mb-0 min-w-[12rem] flex-1 truncate text-2xl font-normal capitalize"
      >
        {{ rangeLabel }}
        <span class="mt-1 block text-xs normal-case text-n-slate-11">
          {{ CALENDAR_TIME_ZONE_LABEL }}
        </span>
      </h2>
      <div class="ml-auto flex shrink-0 items-center gap-2">
        <div v-if="isSearchOpen" class="relative min-w-0">
          <div
            class="relative flex h-11 w-[min(32rem,42vw)] min-w-64 items-center gap-3 rounded-full border border-n-weak bg-n-solid-1 px-4 shadow-sm focus-within:border-n-brand/70"
          >
            <span
              class="pointer-events-none i-lucide-search size-4 text-n-slate-11"
            />
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              type="text"
              class="block h-6 min-w-0 flex-1 appearance-none !rounded-none !border-0 !bg-transparent !p-0 text-sm leading-6 text-n-slate-12 !shadow-none !outline-none !ring-0 placeholder:text-n-slate-9 focus:!border-0 focus:!shadow-none focus:!outline-none focus:!ring-0 focus-visible:!outline-none"
              :aria-label="copy.actions.search"
              :placeholder="copy.actions.search"
              @keydown.esc="closeSearch"
            />
            <button
              v-if="searchQuery"
              type="button"
              class="grid size-6 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
              :aria-label="copy.actions.clearSearch"
              @click="clearSearch"
            >
              <span class="i-lucide-x size-4" />
            </button>
            <button
              v-else
              type="button"
              class="grid size-6 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
              :aria-label="copy.actions.close"
              @click="closeSearch"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>
          <div
            v-if="searchQuery.trim().length >= 2"
            class="absolute right-0 top-[3.25rem] z-[90] max-h-96 w-[26rem] overflow-y-auto rounded-xl border border-n-weak bg-n-solid-1 p-2 shadow-xl"
          >
            <button
              v-for="event in searchResults"
              :key="event.id"
              type="button"
              class="grid w-full gap-1 rounded-lg px-3 py-2 text-left hover:bg-n-alpha-2"
              @click="openCalendarItem(event)"
            >
              <span class="truncate text-sm font-medium text-n-slate-12">
                {{ event.title }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ formatDateTime(event.starts_at) }}
              </span>
            </button>
            <p
              v-if="!searchResults.length && !isRefreshing"
              class="px-3 py-4 text-sm text-n-slate-10"
            >
              {{ copy.empty.search }}
            </p>
          </div>
        </div>
        <button
          v-if="!isSearchOpen"
          type="button"
          class="grid size-10 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
          :aria-label="copy.actions.search"
          @click="toggleSearch"
        >
          <span class="i-lucide-search size-5" />
        </button>
        <button
          type="button"
          class="grid size-10 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
          :class="isRefreshing && 'text-n-brand'"
          :title="copy.actions.sync"
          :disabled="isRefreshing"
          @click="refreshCalendar"
        >
          <span
            class="i-lucide-refresh-cw size-5"
            :class="isRefreshing && 'animate-spin'"
          />
        </button>
        <div v-if="isAdmin" v-on-clickaway="closeFilters" class="relative">
          <button
            type="button"
            class="relative inline-flex h-10 items-center gap-2 rounded-full border border-n-strong px-4 text-sm font-semibold text-n-slate-12 hover:bg-n-alpha-2"
            :class="isFiltersOpen && 'border-n-brand bg-n-alpha-2 text-n-brand'"
            :aria-expanded="isFiltersOpen"
            @click="toggleFilters"
          >
            <span class="i-lucide-list-filter size-4" />
            {{ copy.actions.filters }}
            <span
              v-if="activeFilterCount"
              class="grid min-w-5 place-items-center rounded-full bg-n-brand px-1.5 py-0.5 text-[11px] leading-4 text-white"
            >
              {{ activeFilterCount }}
            </span>
          </button>

          <form
            v-if="isFiltersOpen"
            class="absolute right-0 top-12 z-[90] w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-xl"
            @submit.prevent="applyFilters"
          >
            <div
              class="flex items-start justify-between gap-3 border-b border-n-weak px-4 py-3"
            >
              <div>
                <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{ copy.sections.filters }}
                </h3>
                <p class="mb-0 mt-0.5 text-xs text-n-slate-10">
                  <template v-if="activeFilterCount">
                    {{ activeFilterCount }} {{ copy.labels.activeFilters }}
                  </template>
                  <template v-else>{{ copy.labels.allEvents }}</template>
                </p>
              </div>
              <button
                v-if="activeFilterCount"
                type="button"
                class="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                @click="clearFilters"
              >
                <span class="i-lucide-rotate-ccw size-3.5" />
                {{ copy.actions.clearFilters }}
              </button>
            </div>
            <div class="grid gap-3 p-4 sm:grid-cols-2">
              <label class="grid min-w-0 gap-1.5">
                <span class="field-label">{{ copy.fields.status }}</span>
                <Select
                  v-model="filterDraft.status"
                  :options="filterStatusOptions"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </label>
              <label class="grid min-w-0 gap-1.5">
                <span class="field-label">{{ copy.fields.source }}</span>
                <Select
                  v-model="filterDraft.source"
                  :options="filterSourceOptions"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </label>
              <label class="grid min-w-0 gap-1.5">
                <span class="field-label">{{ copy.fields.assignee }}</span>
                <Select
                  v-model="filterDraft.assignee_id"
                  :options="filterAssigneeOptions"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </label>
              <label class="grid min-w-0 gap-1.5">
                <span class="field-label">{{ copy.fields.inbox }}</span>
                <Select
                  v-model="filterDraft.inbox_id"
                  :options="filterInboxOptions"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </label>
            </div>
            <div
              class="flex justify-end border-t border-n-weak bg-n-alpha-1 px-4 py-3"
            >
              <button type="submit" class="btn btn-primary">
                {{ copy.actions.applyFilters }}
              </button>
            </div>
          </form>
        </div>
        <div class="relative">
          <button
            type="button"
            class="inline-flex h-10 items-center gap-2 rounded-full border border-n-strong px-4 text-sm font-semibold hover:bg-n-alpha-2"
            :class="isViewMenuOpen && 'bg-n-alpha-2 text-n-brand'"
            @click="isViewMenuOpen = !isViewMenuOpen"
          >
            {{ currentViewLabel }}
            <span class="i-lucide-chevron-down size-4" />
          </button>
          <div
            v-if="isViewMenuOpen"
            class="absolute right-0 top-12 z-[80] w-64 rounded-lg border border-n-weak bg-n-solid-1 p-2 shadow-xl"
          >
            <button
              v-for="option in viewOptions"
              :key="option.value"
              type="button"
              class="flex h-11 w-full items-center justify-between rounded-md px-3 text-left text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
              @click="selectViewMode(option.value)"
            >
              <span>{{ option.label }}</span>
              <span
                class="text-xs font-semibold uppercase text-n-slate-10"
                :class="option.value === viewMode && 'text-n-brand'"
              >
                <span
                  v-if="option.value === viewMode"
                  class="i-lucide-check size-4"
                />
                <span v-else>{{ option.shortcut }}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </header>

    <div
      class="grid min-h-0 flex-1 grid-cols-1 overflow-hidden"
      :class="
        isSidePanelOpen
          ? 'lg:grid-cols-[18rem_minmax(0,1fr)]'
          : 'lg:grid-cols-[minmax(0,1fr)]'
      "
    >
      <aside
        class="min-h-0 overflow-y-auto border-r border-n-weak bg-n-solid-1 px-4 py-4"
        :class="isSidePanelOpen ? 'block' : 'hidden'"
      >
        <button
          type="button"
          class="mb-6 inline-flex h-14 items-center gap-3 rounded-2xl border border-n-weak bg-n-solid-2 px-5 text-sm font-semibold shadow-sm transition hover:shadow-md"
          @click="openEventDialog(new Date())"
        >
          <span class="i-lucide-plus size-6" />
          {{ copy.actions.create }}
        </button>

        <section class="mb-6">
          <div class="mb-3 flex items-center justify-between">
            <h3 class="mb-0 text-sm font-semibold capitalize">
              {{ monthLabel }}
            </h3>
            <div class="flex gap-1">
              <button
                type="button"
                class="grid size-7 place-content-center rounded-full hover:bg-n-alpha-2"
                @click="moveMonth(-1)"
              >
                <span class="i-lucide-chevron-left size-4" />
              </button>
              <button
                type="button"
                class="grid size-7 place-content-center rounded-full hover:bg-n-alpha-2"
                @click="moveMonth(1)"
              >
                <span class="i-lucide-chevron-right size-4" />
              </button>
            </div>
          </div>
          <div
            class="grid grid-cols-7 gap-y-1 text-center text-[11px] font-semibold text-n-slate-10"
          >
            <span v-for="weekday in copy.weekdays" :key="weekday">
              {{ weekday.charAt(0) }}
            </span>
            <button
              v-for="day in calendarDays"
              :key="day.key"
              type="button"
              class="mx-auto grid size-8 place-content-center rounded-full text-xs hover:bg-n-alpha-2"
              :class="[
                !day.currentMonth && 'text-n-slate-9',
                day.today && 'bg-n-brand text-white hover:bg-n-brand',
                day.date.toDateString() === currentDate.toDateString() &&
                  !day.today &&
                  'bg-n-alpha-2 text-n-brand',
              ]"
              @click="selectDate(day.date)"
            >
              {{ calendarParts(day.date).day }}
            </button>
          </div>
        </section>

        <aside class="flex flex-col gap-4">
          <section class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
            <h3 class="mb-3 text-base font-semibold text-n-slate-12">
              {{ copy.sections.upcoming }}
            </h3>
            <div v-if="!upcomingEvents.length" class="text-sm text-n-slate-11">
              {{ copy.empty.events }}
            </div>
            <div v-else class="grid gap-2">
              <button
                v-for="event in upcomingEvents"
                :key="event.id"
                type="button"
                class="rounded-lg border bg-n-solid-1 p-3 text-left hover:bg-n-alpha-2"
                :class="
                  event.quote
                    ? 'border-dashed border-n-iris-6'
                    : event.source === 'contract'
                      ? 'border-n-amber-5'
                      : 'border-n-weak'
                "
                @click="openCalendarItem(event)"
              >
                <p
                  class="mb-1 flex items-center gap-1.5 text-sm font-semibold text-n-slate-12"
                >
                  <span
                    v-if="event.quote"
                    class="i-hugeicons-invoice-03 size-3.5 shrink-0 text-n-iris-11"
                  />
                  <span
                    v-else-if="event.source === 'contract'"
                    class="i-lucide-file-text size-3.5 shrink-0 text-n-amber-11"
                  />
                  {{ event.title }}
                </p>
                <p class="mb-1 text-xs text-n-slate-11">
                  {{ formatDateTime(event.starts_at) }}
                </p>
                <span
                  class="rounded-md px-2 py-1 text-xs"
                  :class="eventColor(event)"
                >
                  {{
                    event.quote
                      ? copy.quoteModal.label
                      : eventSourceLabel(event.source)
                  }}
                </span>
              </button>
            </div>
          </section>
        </aside>
      </aside>

      <main class="calendar-main min-w-0 w-full overflow-auto bg-n-solid-2">
        <div
          v-if="['day', 'week', 'fourDays'].includes(viewMode)"
          class="calendar-time-view w-full min-w-[720px]"
        >
          <div
            class="sticky top-0 z-20 grid border-b border-n-weak bg-n-solid-1/95 backdrop-blur"
            :style="{
              gridTemplateColumns: `5rem repeat(${visibleDays.length}, minmax(9rem, 1fr))`,
            }"
          >
            <div class="border-r border-n-weak" />
            <div
              v-for="day in visibleDays"
              :key="day.key"
              class="border-r border-n-weak px-3 py-2 text-center last:border-r-0"
            >
              <div
                class="mb-0.5 text-[11px] font-semibold uppercase tracking-wide"
                :class="day.today ? 'text-n-brand' : 'text-n-slate-10'"
              >
                {{ `${day.weekday}.` }}
              </div>
              <button
                type="button"
                class="mx-auto grid size-10 place-content-center rounded-full text-lg font-semibold"
                :class="
                  day.today
                    ? 'bg-n-brand text-white'
                    : 'text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="selectDate(day.date)"
              >
                {{ day.day }}
              </button>
            </div>
          </div>

          <div
            v-for="hour in timeSlots"
            :key="hour"
            class="grid min-h-[4rem] border-b border-n-weak"
            :style="{
              gridTemplateColumns: `5rem repeat(${visibleDays.length}, minmax(9rem, 1fr))`,
            }"
          >
            <div
              class="border-r border-n-weak pr-3 pt-1 text-right text-xs text-n-slate-10"
            >
              {{ formatHour(hour) }}
            </div>
            <button
              v-for="day in visibleDays"
              :key="`${day.key}-${hour}`"
              type="button"
              class="relative min-h-[4rem] border-r border-n-weak bg-transparent p-1 text-left last:border-r-0 hover:bg-n-alpha-1"
              :class="
                dragOverCalendarKey === calendarDropKey(day.date, hour) &&
                'bg-n-brand/10 ring-1 ring-inset ring-n-brand'
              "
              @click="openEventDialogAt(day.date, hour)"
              @dragover="handleCalendarEventDragOver(day.date, $event, hour)"
              @dragleave="handleCalendarEventDragLeave(day.date, hour)"
              @drop="moveCalendarEventToDate(day.date, $event, hour)"
            >
              <span
                v-for="event in eventsForHour(day.date, hour)"
                :key="event.id"
                class="calendar-event-chip mb-1 block w-full truncate rounded-md px-2 py-1 text-xs font-medium shadow-sm"
                :class="[
                  eventColor(event),
                  isDraggableEvent(event) &&
                    'cursor-grab active:cursor-grabbing',
                ]"
                :draggable="isDraggableEvent(event)"
                @dragstart.stop="startCalendarEventDrag(event, $event)"
                @dragend="clearCalendarDragState"
                @click.stop="openCalendarItem(event)"
              >
                <span class="font-semibold">
                  {{ formatTime(event.starts_at) }}
                </span>
                {{ event.title }}
              </span>
            </button>
          </div>
        </div>

        <div
          v-else-if="viewMode === 'month'"
          class="calendar-month-view w-full min-w-[760px]"
        >
          <div
            class="sticky top-0 z-30 grid grid-cols-7 border-b border-n-weak bg-n-solid-2"
          >
            <div
              v-for="weekday in copy.weekdays"
              :key="weekday"
              class="border-r border-n-weak px-3 py-2 text-center text-xs font-semibold uppercase text-n-slate-10 last:border-r-0"
            >
              {{ weekday }}
            </div>
          </div>
          <div
            v-for="(week, wi) in calendarWeeks"
            :key="wi"
            class="relative border-l border-n-weak"
          >
            <div class="grid grid-cols-7">
              <div
                v-for="day in week"
                :key="day.key"
                role="button"
                tabindex="0"
                class="min-h-40 border-b border-r border-n-weak bg-transparent px-2 py-2 text-left outline-none hover:bg-n-alpha-1 focus:bg-n-alpha-1"
                :class="[
                  !day.currentMonth && 'bg-n-alpha-1/40 text-n-slate-9',
                  dragOverCalendarKey === calendarDropKey(day.date) &&
                    'bg-n-brand/10 ring-1 ring-inset ring-n-brand',
                ]"
                @click="openEventDialogAt(day.date, 9)"
                @dragover="handleCalendarEventDragOver(day.date, $event)"
                @dragleave="handleCalendarEventDragLeave(day.date)"
                @drop="moveCalendarEventToDate(day.date, $event)"
                @keydown.enter="openEventDialogAt(day.date, 9)"
                @keydown.space.prevent="openEventDialogAt(day.date, 9)"
              >
                <div class="flex min-w-0 items-center justify-between gap-1">
                  <time
                    class="grid size-7 shrink-0 place-content-center rounded-full text-sm font-medium"
                    :class="[
                      day.today ? 'bg-n-brand text-white' : 'text-n-slate-12',
                      !day.currentMonth && !day.today && 'text-n-slate-9',
                    ]"
                    :datetime="day.key"
                  >
                    {{ calendarParts(day.date).day }}
                  </time>
                  <span
                    v-if="monthDayTotal(day.date)"
                    class="calendar-day-total inline-flex min-w-0 items-center gap-1 truncate rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-n-slate-12"
                    :title="`${copy.labels.dayTotal}: ${monthDayTotal(day.date)}`"
                  >
                    <i class="i-hugeicons-package size-3.5 shrink-0" />
                    <span class="truncate">{{ monthDayTotal(day.date) }}</span>
                  </span>
                </div>

                <div
                  class="mt-2"
                  :style="{
                    height: `${weekMultiDayRows(wi) * 28}px`,
                  }"
                />

                <div class="space-y-1">
                  <button
                    v-for="note in monthDayNotes(day.date).slice(0, 2)"
                    :key="`${day.key}-note-${note.id}`"
                    type="button"
                    class="calendar-day-note flex h-6 w-full min-w-0 items-center gap-1.5 overflow-hidden rounded-md border border-dashed border-n-strong px-2 text-left text-xs italic text-n-slate-11 hover:bg-n-alpha-2"
                    :title="`${copy.labels.dayNote}: ${note.title}`"
                    :aria-label="`${copy.labels.dayNote}: ${note.title}`"
                    @click.stop="editEvent(note)"
                  >
                    <i class="i-hugeicons-sticky-note-01 size-3.5 shrink-0" />
                    <span class="truncate">{{ note.title }}</span>
                  </button>
                  <button
                    v-for="event in monthVisibleTimedEvents(day.date)"
                    :key="`${day.key}-${event.id}`"
                    type="button"
                    class="calendar-event-chip flex h-6 w-full min-w-0 items-center gap-1.5 overflow-hidden rounded-md px-2 text-left text-xs font-medium"
                    :class="[
                      eventColor(event),
                      isDraggableEvent(event) &&
                        'cursor-grab active:cursor-grabbing',
                    ]"
                    :draggable="isDraggableEvent(event)"
                    :title="event.title"
                    @dragstart.stop="startCalendarEventDrag(event, $event)"
                    @dragend="clearCalendarDragState"
                    @click.stop="openCalendarItem(event)"
                  >
                    <span class="shrink-0 font-semibold">
                      {{ formatTime(event.starts_at) }}
                    </span>
                    <span class="truncate">{{ event.title }}</span>
                  </button>

                  <span
                    v-if="hiddenMonthEventsCount(day.date)"
                    class="block px-2 pt-0.5 text-xs font-medium text-n-slate-10"
                  >
                    {{ `+${hiddenMonthEventsCount(day.date)} mais` }}
                  </span>
                </div>
              </div>
            </div>

            <div
              v-if="weekMultiDayRows(wi)"
              class="pointer-events-none absolute inset-x-0 top-[2.75rem]"
            >
              <button
                v-for="layout in weekLayouts[wi]"
                :key="layout.event.id"
                type="button"
                class="calendar-event-chip pointer-events-auto absolute flex h-6 items-center overflow-hidden px-2 text-left text-xs font-semibold shadow-sm"
                :class="[
                  eventColor(layout.event),
                  layout.startsThisWeek ? 'rounded-l-md' : '',
                  layout.endsThisWeek ? 'rounded-r-md' : '',
                  isDraggableEvent(layout.event) &&
                    'cursor-grab active:cursor-grabbing',
                ]"
                :title="`${layout.event.title} (${multiDayEventLabel(layout.event)})`"
                :draggable="isDraggableEvent(layout.event)"
                :style="{
                  top: `${layout.row * 28}px`,
                  left: `calc(${(layout.startCol / 7) * 100}% + 6px)`,
                  width: `calc(${(layout.colSpan / 7) * 100}% - 12px)`,
                }"
                @dragstart.stop="startCalendarEventDrag(layout.event, $event)"
                @dragend="clearCalendarDragState"
                @click="openCalendarItem(layout.event)"
              >
                <span
                  v-if="layout.startsThisWeek"
                  class="i-lucide-calendar-range mr-1.5 size-3.5 shrink-0"
                  aria-hidden="true"
                />
                <span class="truncate">
                  {{
                    layout.startsThisWeek
                      ? `${layout.event.title} (${multiDayEventLabel(layout.event)})`
                      : ''
                  }}
                </span>
              </button>
            </div>
          </div>
        </div>

        <div
          v-else-if="viewMode === 'year'"
          class="calendar-year-view grid gap-4 p-4 md:grid-cols-3 xl:grid-cols-4"
        >
          <section
            v-for="month in yearMonths"
            :key="month.key"
            class="rounded-lg border border-n-weak bg-n-solid-1 p-3"
          >
            <button
              type="button"
              class="mb-3 text-left text-sm font-semibold capitalize text-n-slate-12 hover:text-n-brand"
              @click="
                currentDate = new Date(month.date);
                viewMode = 'month';
                loadData();
              "
            >
              {{ month.label }}
            </button>
            <div class="space-y-1">
              <button
                v-for="event in eventsForMonth(month.date).slice(0, 4)"
                :key="event.id"
                type="button"
                class="block w-full truncate rounded px-2 py-1 text-left text-xs"
                :class="eventColor(event)"
                @click="openCalendarItem(event)"
              >
                {{ `${calendarParts(event.starts_at).day} · ${event.title}` }}
              </button>
              <p
                v-if="!eventsForMonth(month.date).length"
                class="text-xs text-n-slate-10"
              >
                {{ copy.empty.events }}
              </p>
            </div>
          </section>
        </div>

        <div
          v-else
          class="calendar-schedule-view mx-auto grid max-w-4xl gap-2 p-4"
        >
          <button
            v-for="event in scheduleEvents"
            :key="event.id"
            type="button"
            class="flex items-center gap-4 border-b border-n-weak bg-transparent px-2 py-3 text-left hover:bg-n-alpha-1"
            @click="openCalendarItem(event)"
          >
            <span class="w-36 shrink-0 text-sm text-n-slate-10">
              {{ formatDateTime(event.starts_at) }}
            </span>
            <span class="min-w-0 flex-1 truncate text-sm font-medium">
              {{ event.title }}
            </span>
            <span class="rounded px-2 py-1 text-xs" :class="eventColor(event)">
              {{
                event.quote
                  ? copy.quoteModal.label
                  : eventSourceLabel(event.source)
              }}
            </span>
          </button>
          <p v-if="!scheduleEvents.length" class="p-6 text-sm text-n-slate-11">
            {{ copy.empty.events }}
          </p>
        </div>
      </main>
    </div>

    <Dialog
      ref="eventDialogRef"
      data-testid="event-dialog"
      width="4xl"
      position="center"
      overflow-y-auto
      :title="editingEventId ? copy.actions.edit : copy.actions.newEvent"
      :description="
        editingEventId
          ? 'Atualize os dados e vínculos deste compromisso.'
          : 'Crie um compromisso e conecte-o ao atendimento do cliente.'
      "
      :show-confirm-button="false"
      :show-cancel-button="false"
    >
      <div class="grid min-w-0 gap-4" data-testid="event-form">
        <p
          v-if="isStudioEvent"
          class="mb-0 rounded-lg bg-n-alpha-2 p-3 text-sm text-n-slate-11"
          role="status"
        >
          {{ copy.messages.studioReadonly }}
        </p>
        <p
          v-if="isGoogleReadonly"
          class="mb-0 rounded-lg bg-n-alpha-2 p-3 text-sm text-n-slate-11"
          role="status"
        >
          {{ copy.messages.googleReadonly }}
        </p>
        <div
          class="flex flex-wrap items-center gap-2 rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm"
        >
          <span
            class="size-2 shrink-0 rounded-full"
            :class="statusDotClass(eventForm.status)"
          />
          <span class="font-medium text-n-slate-12">
            {{ eventSummary || copy.placeholders.period }}
          </span>
          <span
            v-if="selectedContact"
            class="inline-flex min-w-0 items-center gap-1 text-n-slate-11"
          >
            <span class="i-hugeicons-user size-3.5 shrink-0" />
            <span class="truncate">{{
              selectedContact.name || selectedContact.phone_number
            }}</span>
          </span>
          <span
            class="ml-auto rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
          >
            {{ copy.sources[eventForm.source] || eventForm.source }}
          </span>
        </div>

        <fieldset
          class="m-0 grid min-w-0 gap-4 border-0 p-0 lg:grid-cols-[minmax(0,1fr)_17.5rem] lg:items-start"
          :disabled="isEventBusy || isStudioEvent"
        >
          <div class="grid min-w-0 gap-4">
            <section class="event-card">
              <h4 class="event-card__title">
                <span class="i-hugeicons-calendar-03 event-card__icon" />
                {{ copy.sections.eventDetails }}
              </h4>
              <Input
                v-model="eventForm.title"
                required
                :label="copy.fields.title"
                :placeholder="copy.placeholders.title"
              />
              <EventDateTimeFields
                :key="editingEventId || 'new-event'"
                :starts-at="eventForm.starts_at"
                :ends-at="eventForm.ends_at"
                :all-day="eventForm.all_day"
                :min-date="calendarPickerDate(new Date())"
                :disabled="isEventBusy || isStudioEvent"
                @change="updateEventPeriod"
                @validity-change="eventPeriodValid = $event"
              />
            </section>

            <section class="event-card">
              <h4 class="event-card__title">
                <span class="i-hugeicons-user-account event-card__icon" />
                {{ copy.fields.contact }}
              </h4>

              <div
                v-if="selectedContact && !isChangingContact"
                class="flex min-w-0 items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
                data-testid="selected-contact"
              >
                <span
                  class="grid size-10 shrink-0 place-items-center rounded-full bg-n-brand/10 text-sm font-semibold text-n-brand"
                >
                  {{ contactInitials(selectedContact) }}
                </span>
                <div class="min-w-0 flex-1">
                  <p class="mb-0 truncate text-sm font-medium text-n-slate-12">
                    {{
                      selectedContact.name ||
                      selectedContact.phone_number ||
                      selectedContact.email
                    }}
                  </p>
                  <p class="mb-0 truncate text-xs text-n-slate-11">
                    {{ selectedContact.phone_number || selectedContact.email }}
                  </p>
                  <a
                    v-if="editingConversationUrl"
                    :href="editingConversationUrl"
                    class="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
                  >
                    <span class="i-hugeicons-message-01 size-3.5" />
                    {{
                      `${copy.actions.openConversation} #${editingConversationDisplayId}`
                    }}
                  </a>
                </div>
                <div class="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    class="rounded-lg px-2 py-1 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-2"
                    @click="startChangingContact"
                  >
                    {{ copy.actions.changeContact }}
                  </button>
                  <button
                    type="button"
                    class="grid size-7 place-items-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-ruby-11"
                    :aria-label="copy.actions.removeContact"
                    @click="clearContact"
                  >
                    <span class="i-hugeicons-cancel-01 size-4" />
                  </button>
                </div>
              </div>

              <div v-if="showContactSearch" class="relative grid gap-1.5">
                <Input
                  v-model="contactSearch"
                  :label="copy.fields.contactSearch"
                  :placeholder="copy.placeholders.contactSearch"
                  data-testid="contact-search"
                  @input="searchContacts"
                />
                <button
                  v-if="isChangingContact"
                  type="button"
                  class="w-fit text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
                  @click="cancelChangingContact"
                >
                  {{ copy.actions.cancel }}
                </button>
                <div
                  v-if="contactResults.length"
                  class="absolute left-0 right-0 top-[4.75rem] z-40 max-h-56 overflow-auto rounded-xl border border-n-weak bg-n-solid-1 p-1 shadow-xl"
                  data-testid="contact-results"
                >
                  <button
                    v-for="contact in contactResults"
                    :key="contact.id"
                    type="button"
                    class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-n-alpha-2"
                    @click="selectContact(contact)"
                  >
                    <span
                      class="grid size-7 shrink-0 place-items-center rounded-full bg-n-alpha-2 text-[11px] font-semibold text-n-slate-11"
                    >
                      {{ contactInitials(contact) }}
                    </span>
                    <span class="min-w-0">
                      <span class="block truncate font-medium text-n-slate-12">
                        {{
                          contact.name || contact.phone_number || contact.email
                        }}
                      </span>
                      <span class="block truncate text-xs text-n-slate-10">
                        {{
                          contact.phone_number ||
                          contact.email ||
                          contact.identifier
                        }}
                      </span>
                    </span>
                  </button>
                </div>
              </div>

              <div
                v-if="!editingEventId && selectedContact"
                class="grid gap-1.5"
              >
                <span class="field-label">{{
                  copy.actions.sendConversation
                }}</span>
                <span
                  v-if="conversationsLoading"
                  class="text-xs text-n-slate-11"
                >
                  {{ copy.actions.loadingConversations }}
                </span>
                <Select
                  v-else
                  v-model="sendConversationId"
                  :options="conversationOptions"
                  :aria-label="copy.actions.sendConversation"
                  class="w-full [&>select]:w-full"
                  data-testid="event-send-conversation"
                />
                <p
                  v-if="
                    conversationLoadError ||
                    (!conversationsLoading && !contactConversations.length)
                  "
                  class="m-0 text-xs text-n-slate-11"
                >
                  {{
                    conversationLoadError ||
                    'Nenhuma conversa disponível para este contato e esta caixa. Você pode salvar o evento sem enviar.'
                  }}
                </p>
              </div>
              <p
                v-if="sendError"
                role="alert"
                class="m-0 text-sm text-n-amber-11"
                data-testid="event-send-error"
              >
                {{ sendError }}
              </p>
              <div class="grid min-w-0 gap-3 sm:grid-cols-2">
                <div class="grid min-w-0 gap-1.5">
                  <span class="field-label">{{ copy.fields.inbox }}</span>
                  <Select
                    v-model="eventForm.inbox_id"
                    :options="eventInboxOptions"
                    class="w-full [&>select]:h-10 [&>select]:w-full"
                  />
                </div>
                <Input
                  v-model="eventForm.location"
                  :label="copy.fields.location"
                  :placeholder="copy.placeholders.location"
                />
              </div>
            </section>

            <section class="event-card">
              <label class="grid gap-1.5">
                <span class="event-card__title">
                  <span class="i-hugeicons-note-edit event-card__icon" />
                  {{ copy.sections.notes }}
                </span>
                <textarea
                  v-model="eventForm.description"
                  rows="3"
                  class="form-input min-h-24 resize-y"
                  :placeholder="copy.placeholders.notes"
                />
              </label>
            </section>
          </div>

          <aside class="grid min-w-0 gap-4">
            <section class="event-card">
              <h4 class="event-card__title">
                <span class="i-hugeicons-task-done-01 event-card__icon" />
                {{ copy.fields.status }}
              </h4>
              <div
                class="grid grid-cols-2 gap-1.5"
                role="radiogroup"
                :aria-label="copy.fields.status"
              >
                <button
                  v-for="option in statusOptions"
                  :key="option.value"
                  type="button"
                  role="radio"
                  :aria-checked="eventForm.status === option.value"
                  class="flex min-w-0 items-center gap-1.5 rounded-lg border px-2.5 py-2 text-left text-xs font-medium transition-colors"
                  :class="
                    eventForm.status === option.value
                      ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
                  "
                  :data-testid="`status-${option.value}`"
                  @click="eventForm.status = option.value"
                >
                  <span
                    class="size-2 shrink-0 rounded-full"
                    :class="statusDotClass(option.value)"
                  />
                  <span class="leading-tight">{{ option.label }}</span>
                </button>
              </div>
              <div class="grid min-w-0 gap-1.5">
                <span class="field-label">{{ copy.fields.assignee }}</span>
                <Select
                  v-model="eventForm.assignee_id"
                  :options="assigneeOptions"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </div>
              <div class="grid min-w-0 gap-1.5">
                <span class="field-label">{{ copy.fields.source }}</span>
                <Select
                  v-model="eventForm.source"
                  :options="sourceOptions"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </div>
            </section>

            <section class="event-card">
              <h4 class="event-card__title">
                <span class="i-hugeicons-package event-card__icon" />
                {{ copy.sections.service }}
              </h4>
              <div class="grid min-w-0 gap-1.5">
                <span class="field-label">{{
                  copy.fields.inventoryProduct
                }}</span>
                <Select
                  v-model="eventForm.metadata.inventory_product_id"
                  :options="productOptions"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </div>
              <Input
                v-model="eventForm.metadata.inventory_quantity"
                type="number"
                min="1"
                :label="copy.fields.inventoryQuantity"
                :placeholder="copy.placeholders.inventoryQuantity"
              />
            </section>

            <section class="event-card">
              <div class="flex items-center justify-between gap-3">
                <h4 class="event-card__title">
                  <span class="i-hugeicons-video-01 event-card__icon" />
                  {{ copy.sections.googleMeet }}
                </h4>
                <Switch
                  v-model="eventForm.metadata.create_google_meet"
                  @change="handleGoogleMeetToggle"
                />
              </div>
              <span
                class="inline-flex w-fit items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
                :class="
                  isGoogleCalendarActive
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-amber-3 text-n-amber-11'
                "
              >
                <span
                  class="size-1.5 rounded-full"
                  :class="
                    isGoogleCalendarActive ? 'bg-n-teal-9' : 'bg-n-amber-9'
                  "
                />
                {{
                  isGoogleCalendarActive
                    ? copy.labels.connected
                    : copy.labels.googleUnavailable
                }}
              </span>
              <div
                v-if="
                  integrationLoadFailed ||
                  (eventForm.metadata.create_google_meet &&
                    !isGoogleCalendarActive)
                "
                class="grid gap-2 rounded-lg border border-n-amber-5 bg-n-amber-2 p-2.5 text-xs text-n-amber-11"
              >
                <span>
                  {{
                    integrationLoadFailed
                      ? copy.messages.googleIntegrationLoadFailed
                      : copy.messages.googleIntegrationRequired
                  }}
                </span>
                <button
                  v-if="isAdmin"
                  type="button"
                  class="w-fit font-medium text-n-amber-12 underline"
                  @click="openGoogleCalendarSettings"
                >
                  {{ copy.actions.configureGoogle }}
                </button>
              </div>
              <Input
                v-if="eventForm.metadata.create_google_meet"
                v-model="eventForm.metadata.attendee_emails"
                :label="copy.fields.attendeeEmails"
                :placeholder="copy.placeholders.attendeeEmails"
              />
              <a
                v-if="googleMeetLink"
                :href="googleMeetLink"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-n-brand hover:underline"
              >
                <span class="i-hugeicons-link-square-02 size-4" />
                {{ copy.fields.googleMeetLink }}
              </a>
            </section>
          </aside>
        </fieldset>

        <div
          class="sticky -bottom-6 z-30 -mx-6 -mb-6 flex w-auto flex-wrap justify-end gap-3 border-t border-n-weak bg-n-solid-1/95 px-6 py-4 backdrop-blur"
        >
          <Button
            v-if="editingEventId && !isStudioEvent"
            type="button"
            color="ruby"
            variant="ghost"
            icon="i-lucide-trash-2"
            :label="copy.actions.deleteEvent"
            class="sm:mr-auto"
            :disabled="isEventBusy || !canDeleteEvent"
            data-testid="delete-event"
            @click="openDeleteDialog"
          />
          <Button
            type="button"
            color="slate"
            variant="faded"
            :label="copy.actions.cancel"
            :disabled="isEventBusy"
            @click="eventDialogRef?.close()"
          />
          <Button
            type="button"
            icon="i-hugeicons-floppy-disk"
            :label="copy.actions.saveEvent"
            :is-loading="isSaving"
            :disabled="
              isEventBusy ||
              isStudioEvent ||
              !eventPeriodValid ||
              !eventForm.title ||
              !eventForm.starts_at
            "
            data-testid="save-event"
            @click="saveEvent()"
          />
          <Button
            v-if="!editingEventId"
            type="button"
            icon="i-hugeicons-sent"
            label="Criar e enviar na conversa"
            :is-loading="isSaving"
            :disabled="
              isEventBusy ||
              !eventPeriodValid ||
              !eventForm.title.trim() ||
              !sendConversation ||
              sendConversation.can_reply === false ||
              eventForm.status !== 'scheduled'
            "
            data-testid="create-and-send-event"
            @click="saveEvent(true)"
          />
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      data-testid="delete-event-dialog"
      type="alert"
      :title="copy.actions.deleteEvent"
      :description="`Excluir “${eventForm.title}”? Esta ação não pode ser desfeita. Se o evento estiver sincronizado, ele também será excluído do Google Agenda.`"
      :show-confirm-button="false"
      :show-cancel-button="false"
      :close-on-click-outside="false"
    >
      <p
        v-if="deleteError"
        class="text-sm text-n-ruby-11"
        role="alert"
        data-testid="delete-event-error"
      >
        {{ deleteError }}
      </p>
      <template #footer>
        <div class="flex flex-wrap justify-end gap-3">
          <Button
            type="button"
            color="slate"
            variant="faded"
            label="Cancelar"
            :disabled="isDeleting"
            @click="deleteDialogRef?.close()"
          />
          <Button
            type="button"
            color="ruby"
            :label="copy.actions.deleteEvent"
            :disabled="isDeleting || !canDeleteEvent"
            :is-loading="isDeleting"
            data-testid="confirm-delete-event"
            @click="deleteEvent"
          />
        </div>
      </template>
    </Dialog>

    <Teleport to="body">
      <div
        v-if="showContractModal && selectedContract"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 p-4 backdrop-blur-md"
        @click.self="showContractModal = false"
        @keydown.esc="showContractModal = false"
      >
        <section
          class="calendar-contract-modal flex max-h-[calc(100dvh-2rem)] w-full max-w-xl flex-col overflow-hidden rounded-2xl border bg-n-solid-2 shadow-2xl"
          :class="
            isQuoteSelected ? 'border-t-4 border-n-iris-6' : 'border-n-weak'
          "
          role="dialog"
          aria-modal="true"
          aria-labelledby="calendar-contract-title"
        >
          <header
            class="flex shrink-0 items-start gap-3 border-b border-n-weak px-5 py-4"
          >
            <span
              class="grid size-10 shrink-0 place-content-center rounded-xl"
              :class="
                isQuoteSelected
                  ? 'bg-n-iris-3 text-n-iris-11'
                  : 'bg-n-amber-3 text-n-amber-11'
              "
              aria-hidden="true"
            >
              <i
                :class="
                  isQuoteSelected
                    ? 'i-hugeicons-invoice-03'
                    : 'i-hugeicons-contracts'
                "
                class="size-5"
              />
            </span>
            <div class="min-w-0 flex-1">
              <p
                v-if="isQuoteSelected"
                class="mb-0.5 text-xs font-semibold uppercase tracking-wide text-n-iris-11"
              >
                {{ `${copy.quoteModal.label} · #${selectedContract.quote_id}` }}
              </p>
              <p
                v-else
                class="mb-0.5 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
              >
                {{ copy.contractModal.label }}
                <template v-if="selectedContract.template_name">
                  {{ `· ${selectedContract.template_name}` }}
                </template>
              </p>
              <h2
                id="calendar-contract-title"
                class="mb-2 truncate text-lg font-semibold text-n-slate-12"
                :title="selectedContract.recipient_name"
              >
                {{ selectedContract.recipient_name }}
              </h2>
              <div class="flex flex-wrap gap-1.5">
                <span
                  v-if="isQuoteSelected"
                  class="inline-flex items-center rounded-full border border-dashed border-n-iris-6 bg-n-iris-3 px-2.5 py-0.5 text-xs font-medium text-n-iris-11"
                >
                  {{
                    copy.quoteModal.statuses[selectedContract.status] ||
                    selectedContract.status
                  }}
                </span>
                <span
                  v-else
                  class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium"
                  :class="contractStatusColor(selectedContract.status)"
                >
                  {{ contractStatusLabel(selectedContract.status) }}
                </span>
                <span
                  v-if="selectedContractEventType"
                  class="inline-flex items-center gap-1 rounded-full border border-n-weak bg-n-alpha-2 px-2.5 py-0.5 text-xs font-medium text-n-slate-12"
                >
                  <i
                    :class="
                      selectedContractEventType === 'pickup'
                        ? 'i-hugeicons-package-receive'
                        : 'i-hugeicons-delivery-truck-01'
                    "
                    class="size-3.5"
                  />
                  {{ contractEventTypeLabel(selectedContractEventType) }}
                </span>
              </div>
            </div>
            <button
              type="button"
              class="grid size-9 shrink-0 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              :aria-label="copy.contractModal.fechar"
              @click="showContractModal = false"
            >
              <i class="i-hugeicons-cancel-01 size-5" />
            </button>
          </header>

          <div class="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <div class="grid gap-2 sm:grid-cols-3">
              <div
                class="rounded-xl border p-3"
                :class="
                  selectedContractEventType === 'delivery'
                    ? 'border-n-brand bg-n-brand/5'
                    : 'border-n-weak bg-n-alpha-1'
                "
              >
                <p
                  class="mb-1 flex items-center gap-1.5 text-xs text-n-slate-10"
                >
                  <i class="i-hugeicons-delivery-truck-01 size-3.5" />
                  {{ copy.contractModal.dataEntrega }}
                </p>
                <p class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{
                    selectedContract.scheduled_at
                      ? formatDateTime(selectedContract.scheduled_at)
                      : copy.labels.emptyValue
                  }}
                </p>
              </div>
              <div
                class="rounded-xl border p-3"
                :class="
                  selectedContractEventType === 'pickup'
                    ? 'border-n-brand bg-n-brand/5'
                    : 'border-n-weak bg-n-alpha-1'
                "
              >
                <p
                  class="mb-1 flex items-center gap-1.5 text-xs text-n-slate-10"
                >
                  <i class="i-hugeicons-package-receive size-3.5" />
                  {{ copy.contractModal.dataRetirada }}
                </p>
                <p class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{
                    selectedContract.pickup_at
                      ? formatDateTime(selectedContract.pickup_at)
                      : copy.labels.emptyValue
                  }}
                </p>
              </div>
              <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-3">
                <p
                  class="mb-1 flex items-center gap-1.5 text-xs text-n-slate-10"
                >
                  <i class="i-hugeicons-money-bag-02 size-3.5" />
                  {{ copy.contractModal.valorTotal }}
                </p>
                <p
                  class="mb-0 text-sm font-semibold tabular-nums text-n-slate-12"
                >
                  {{
                    selectedContract.metadata?.invoice_total
                      ? formatContractMoney(
                          selectedContract.metadata.invoice_total
                        )
                      : copy.labels.emptyValue
                  }}
                </p>
                <p
                  v-if="selectedContract.payment_method"
                  class="mb-0 mt-0.5 text-xs text-n-slate-11"
                >
                  {{ selectedContract.payment_method }}
                </p>
              </div>
            </div>

            <section class="mt-5">
              <h3
                class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
              >
                {{ copy.contractModal.cliente }}
              </h3>
              <div
                class="flex items-center gap-3 rounded-xl border border-n-weak p-3"
              >
                <span
                  class="grid size-9 shrink-0 place-content-center rounded-full bg-n-alpha-2 text-n-slate-11"
                  aria-hidden="true"
                >
                  <i class="i-hugeicons-user size-4" />
                </span>
                <div class="min-w-0 flex-1">
                  <p class="mb-0 truncate text-sm font-medium text-n-slate-12">
                    {{
                      selectedContract.contact_name ||
                      selectedContract.recipient_name
                    }}
                  </p>
                  <p
                    v-if="selectedContract.recipient_phone"
                    class="mb-0 text-xs tabular-nums text-n-slate-11"
                  >
                    {{ formatContactPhone(selectedContract.recipient_phone) }}
                  </p>
                </div>
                <button
                  v-if="selectedContract.contact_id"
                  type="button"
                  class="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-n-weak px-2.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-2"
                  @click="goToContact(selectedContract.contact_id)"
                >
                  <i class="i-hugeicons-user-search-01 size-4" />
                  {{ copy.contractModal.verContato }}
                </button>
              </div>
            </section>

            <section v-if="selectedContactAddress" class="mt-5">
              <h3
                class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
              >
                {{ copy.contractModal.endereco }}
              </h3>
              <div class="rounded-xl border border-n-weak p-3">
                <div class="flex items-start gap-2">
                  <i
                    class="i-hugeicons-location-01 mt-0.5 size-4 shrink-0 text-n-slate-10"
                  />
                  <p
                    class="mb-0 min-w-0 flex-1 break-words text-sm text-n-slate-12"
                  >
                    {{ selectedContactAddress }}
                  </p>
                  <button
                    v-tooltip.top="copy.contractModal.copiarEndereco"
                    type="button"
                    class="relative grid size-8 shrink-0 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-brand"
                    @click="copyContractAddress"
                  >
                    <i class="i-hugeicons-copy-01 size-4" />
                    <span class="sr-only">
                      {{ copy.contractModal.copiarEndereco }}
                    </span>
                  </button>
                </div>
                <div
                  class="mt-3 grid gap-2"
                  :class="isQuoteSelected ? 'sm:grid-cols-2' : 'sm:grid-cols-3'"
                >
                  <a
                    :href="mapUrl('google')"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
                  >
                    <i class="i-hugeicons-maps-location-01 size-4" />
                    {{ copy.contractModal.abrirGoogleMaps }}
                  </a>
                  <a
                    :href="mapUrl('waze')"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
                  >
                    <i class="i-hugeicons-navigation-03 size-4" />
                    {{ copy.contractModal.abrirWaze }}
                  </a>
                  <button
                    v-if="!isQuoteSelected"
                    type="button"
                    class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-brand px-3 text-sm font-medium text-n-brand hover:bg-n-brand/10"
                    @click="openRouteSendModal"
                  >
                    <i class="i-hugeicons-sent size-4" />
                    {{ copy.contractModal.enviarRota }}
                  </button>
                </div>
              </div>
            </section>

            <section v-if="selectedContractItems.length" class="mt-5">
              <div class="mb-2 flex items-baseline justify-between gap-2">
                <h3
                  class="mb-0 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
                >
                  {{ copy.contractModal.produtos }}
                </h3>
                <span class="text-xs tabular-nums text-n-slate-11">
                  {{ contractItemsSummary }}
                </span>
              </div>
              <ul
                class="divide-y divide-n-weak overflow-hidden rounded-xl border border-n-weak"
              >
                <li
                  v-for="(item, idx) in selectedContractItems"
                  :key="idx"
                  class="flex items-center gap-3 px-3 py-2.5"
                >
                  <span
                    class="grid h-7 min-w-9 shrink-0 place-content-center rounded-md bg-n-alpha-2 px-1.5 text-xs font-semibold tabular-nums text-n-slate-12"
                    :aria-label="`${copy.contractModal.qtd} ${item.quantity}`"
                  >
                    {{ `${item.quantity}×` }}
                  </span>
                  <div class="min-w-0 flex-1">
                    <p
                      class="mb-0 truncate text-sm font-medium text-n-slate-12"
                    >
                      {{ item.product_name || '-' }}
                    </p>
                    <p class="mb-0 text-xs tabular-nums text-n-slate-10">
                      {{ formatContractMoney(item.unit_price) }}
                      {{ copy.contractModal.porUnidade }}
                    </p>
                  </div>
                  <span
                    class="shrink-0 text-sm font-semibold tabular-nums text-n-slate-12"
                  >
                    {{
                      formatContractMoney(
                        isQuoteSelected && item.total != null
                          ? item.total
                          : Number(item.quantity || 0) *
                              Number(item.unit_price || 0)
                      )
                    }}
                  </span>
                </li>
              </ul>
            </section>
          </div>

          <footer
            class="flex shrink-0 flex-col gap-3 border-t border-n-weak bg-n-solid-2 px-5 py-4"
          >
            <div
              v-if="isQuoteSelected"
              class="flex flex-wrap items-center justify-end gap-2"
            >
              <Button
                :label="copy.contractModal.fechar"
                variant="ghost"
                color="slate"
                @click="showContractModal = false"
              />
              <Button
                v-if="!selectedContract.contract_id"
                :label="copy.quoteModal.editar"
                icon="i-hugeicons-pencil-edit-02"
                variant="outline"
                color="slate"
                @click="goToQuote('edit')"
              />
              <Button
                v-if="!selectedContract.contract_id"
                :label="copy.quoteModal.criarContrato"
                icon="i-hugeicons-contracts"
                @click="goToQuote('convert')"
              />
            </div>
            <div v-if="!isQuoteSelected" class="flex flex-wrap items-end gap-2">
              <label class="grid min-w-0 flex-1 gap-1.5">
                <span class="text-xs font-semibold text-n-slate-10">
                  {{ copy.contractModal.alterarStatus }}
                </span>
                <Select
                  v-model="pendingContractStatus"
                  :options="CONTRACT_STATUSES"
                  :aria-label="copy.contractModal.alterarStatus"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </label>
              <Button
                :label="copy.contractModal.confirmar"
                :is-loading="contractStatusChanging"
                :disabled="
                  contractStatusChanging ||
                  pendingContractStatus === selectedContract.status
                "
                class="h-10"
                @click="saveContractStatus"
              />
            </div>
            <div
              v-if="!isQuoteSelected"
              class="flex flex-wrap items-center justify-end gap-2"
            >
              <Button
                :label="copy.contractModal.fechar"
                variant="ghost"
                color="slate"
                @click="showContractModal = false"
              />
              <Button
                :label="copy.contractModal.editarContrato"
                icon="i-hugeicons-pencil-edit-02"
                variant="outline"
                color="slate"
                @click="goToContractEdit"
              />
              <a
                v-if="selectedContract.public_url"
                :href="selectedContract.public_url"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-medium text-white hover:brightness-95"
              >
                <i class="i-hugeicons-link-square-02 size-4" />
                {{ copy.contractModal.verContrato }}
              </a>
            </div>
          </footer>
        </section>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="showRouteSendModal && selectedContract"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-md"
        @click.self="closeRouteSendModal"
      >
        <section
          class="flex w-full max-w-md flex-col overflow-visible rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
          role="dialog"
          aria-modal="true"
        >
          <header
            class="flex items-start justify-between gap-4 border-b border-n-weak px-6 py-5"
          >
            <div class="min-w-0">
              <p
                class="mb-1 text-xs font-semibold uppercase tracking-wide text-n-amber-11"
              >
                {{ copy.contractModal.mapas }}
              </p>
              <h2 class="text-lg font-semibold text-n-slate-12">
                {{ copy.contractModal.enviarRota }}
              </h2>
            </div>
            <button
              type="button"
              class="grid size-9 shrink-0 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              @click="closeRouteSendModal"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </header>

          <div class="grid gap-4 px-6 py-5">
            <div class="relative">
              <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                {{ copy.contractModal.enviarPara }}
                <input
                  v-model="mapContactSearch"
                  class="h-10 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand"
                  :placeholder="copy.contractModal.buscarContato"
                  @input="handleMapContactSearch"
                />
              </label>
              <div
                v-if="mapContactResults.length"
                class="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-n-weak bg-n-solid-2 shadow-lg"
              >
                <button
                  v-for="contact in mapContactResults"
                  :key="contact.id"
                  type="button"
                  class="grid w-full gap-0.5 border-b border-n-weak px-3 py-2 text-left text-sm last:border-b-0 hover:bg-n-alpha-2"
                  @click="selectMapContact(contact)"
                >
                  <span class="font-medium text-n-slate-12">
                    {{ contact.name || 'Sem nome' }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{ contact.phone_number || '-' }}
                  </span>
                </button>
              </div>
              <p
                v-if="isSearchingMapContacts"
                class="mb-0 mt-1 text-xs text-n-slate-10"
              >
                {{ copy.contractModal.buscandoContatos }}
              </p>
            </div>

            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              {{ copy.contractModal.caixaEntrada }}
              <select
                v-model="selectedMapInboxId"
                class="calendar-select h-10 w-full appearance-none rounded-lg border border-n-weak bg-n-solid-2 px-3 pr-10 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand"
              >
                <option value="">
                  {{ copy.contractModal.selecionarCaixaEntrada }}
                </option>
                <option
                  v-for="inbox in inboxOptions"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ getInboxLabel(inbox) }}
                </option>
              </select>
            </label>

            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              {{ copy.contractModal.mapas }}
              <select
                v-model="mapProviderToSend"
                class="calendar-select h-10 w-full appearance-none rounded-lg border border-n-weak bg-n-solid-2 px-3 pr-10 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand"
              >
                <option
                  v-for="option in mapProviderOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
          </div>

          <footer
            class="flex items-center justify-end gap-3 border-t border-n-weak px-6 py-4"
          >
            <button
              type="button"
              class="inline-flex h-10 w-full items-center justify-center rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 sm:w-auto"
              @click="closeRouteSendModal"
            >
              {{ copy.contractModal.fechar }}
            </button>
            <button
              type="button"
              class="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-medium text-white hover:brightness-95 disabled:opacity-60"
              :disabled="sendMapDisabled"
              @click="sendMapToContact"
            >
              <span class="i-lucide-send size-4" />
              {{ copy.contractModal.enviarRota }}
            </button>
          </footer>
        </section>
      </div>
    </Teleport>
  </section>
</template>

<style scoped>
.event-card {
  @apply grid min-w-0 gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4;
}

.event-card__title {
  @apply mb-0 flex items-center gap-2 text-sm font-semibold text-n-slate-12;
}

.event-card__icon {
  @apply size-4 shrink-0 text-n-brand;
}

.field-label {
  @apply mb-0.5 text-sm font-medium text-n-slate-12;
}
</style>
